import { api, qs } from './client'

// ---------------------------------------------------------------------------
// 產品（憑證設計＋套裝）——批93 §2.1 F
// ---------------------------------------------------------------------------

/** scoped 模組的一個子項（進線通路的 `line`、AI 的 `gemini`…）。 */
export interface ModuleItemSpec {
  key: string
  label: string
  /** false＝列在標籤表裡但**不可勾選**（CS 的 `manual` 人工進線）。既有授權可能帶著它，
   *  所以顯示名稱仍要查得到——只是新證不給勾。 */
  selectable: boolean
}

export interface ModuleSpec {
  key: string
  label: string
  /** `binary`（有鍵就是全開）｜`scoped`（值可為 `*` 或子項 csv 白名單）。 */
  kind: 'binary' | 'scoped' | string
  items: ModuleItemSpec[]
  /** 勾選啟用時寫進 entitlement map 的值（現行一律 `*`）。 */
  default_scope: string
  /** 不再販售但舊證還帶著——不計入未知鍵警告，UI 標「已停用」。 */
  deprecated: boolean
  /**
   * 批155：內部模組——不出現在任何對外投影、外部申請路徑帶到一律 422；後台只有營運主管以上
   * 能把它放進核發／換證／審核改內容（其他角色 403 `internal_plan_requires_manager`）。
   * 後端只在 true 時輸出這個鍵，缺鍵＝false。判讀一律走 `lib/products.ts::isInternalModule`。
   */
  internal?: boolean
}

export interface QuotaSpec {
  key: string
  /** 鍵缺席時產品端怎麼解讀：null＝不限、0＝當成 0（CS 的 `partner_seats`）。 */
  absent_default: number | null
  label: string
  /** 用量從心跳的哪個欄位讀：null＝`quotas_in_use[key]`、`"partner_seats_in_use"`＝專屬欄。 */
  usage_source: string | null
  hint: string | null
  deprecated: boolean
  /**
   * 批114：簽發時**必須有值**（缺鍵＝422 `quota_required`）。
   * 舊憑證設計沒有這個鍵 → 缺鍵預設 **false**（不必填）。判讀一律走
   * `lib/quotas.ts::quotaRequired`，不要在呼叫端各自 `?? false`。
   */
  required?: boolean
  /**
   * 批114：值可不可以是 `null`（無上限）。false＝不得為 null，表單不給「無上限」勾選。
   * 舊憑證設計沒有這個鍵 → 缺鍵預設 **true**（可無上限）。走 `quotaAllowsUnlimited`。
   */
  allow_unlimited?: boolean
  /**
   * 批114：客戶在自助 portal 的申請單裡可不可以填這個鍵。
   * 舊憑證設計沒有這個鍵 → 缺鍵預設 **false**（不可申請）。走 `quotaCustomerRequestable`。
   */
  customer_requestable?: boolean
  /**
   * 批151（裁示 98）：主證與生效子證的同名配額怎麼合併——`max`｜`min`｜`sum`｜`avg`｜`latest`。
   * 後端投影**只在非預設值時出現**，缺鍵＝`max`。判讀一律走 `lib/quotas.ts::quotaAddonMerge`。
   */
  addon_merge?: string
  /**
   * 批169（裁示 81）：用量告警門檻（百分比，遞增、1～100、最多 4 個）。部署用量越過門檻時 LS 產生
   * `quota_usage` 告警（只留越過的最高一級：100% 以上嚴重、其餘警告；降回門檻以下自動解除）。
   * 後端投影**只在非空時出現**；缺鍵＝不告警。
   */
  usage_alert_pcts?: number[]
  /**
   * 批169：精確用量的來源＝站台指標（`deployments.metrics`）的鍵，與配額同單位、可帶小數。
   * 心跳的整數用量在小上限時會失真，宣告了這個鍵，告警與部署頁使用率就改讀它（沒回報時退回整數用量）。
   * 只在有值時出現。判讀走 `lib/quotas.ts::usedOf`。
   */
  usage_metric?: string
  /**
   * 加購數量級距（11-01 裁示）：加購子證上這個鍵的值必須是它的正整數倍（100＝只能 100、200、300…）。
   * 後端投影**只在有宣告時出現**；缺鍵＝不檢查。值域 2～1000000000（1 等同不檢查，存檔時正規化掉）。
   * 判讀一律走 `lib/quotas.ts::quotaAddonStep`；不合時後端回 422 `addon_quota_step`（帶 `quota`／`step`／`value`）。
   */
  addon_step?: number
}

/**
 * 行為參數的一個鍵（批109）。與 `QuotaSpec` 同構，但語意是**第三種 entitlement**
 * ——不是能力開關（modules）也不是數量上限（quotas），而是「這套站台怎麼跑」。
 *
 * ★ 與 `PlanSpec.activation_policy`（核發審核政策）**完全無關**，只是撞字。
 */
export interface PolicySpec {
  key: string
  label: string
  /** `number`｜`bool`｜`string`——決定後台表單的控制項與簽發時的值域檢查。 */
  type: string
  /** 鍵缺席時產品端怎麼解讀（型別要與 `type` 相符）。 */
  absent_default: number | boolean | string | null
  /** 僅 `string`：後台下拉的建議值。**不進 payload**。 */
  options?: string[]
  /** 僅 `number`：後台表單的數值範圍。**不進 payload**。 */
  min?: number | null
  max?: number | null
  hint: string | null
  deprecated: boolean
  /**
   * 批151（裁示 112）：僅 `string`——核發表單在這一格旁邊給「產生」鈕（`lib/generatedValue.ts`）。
   * 勾了之後可選值變成選填（空清單＝自由字串）。後端投影只在 true 時出現；**不進 payload**。
   */
  generatable?: boolean
}

export interface PlanSpec {
  /** 套裝列的 uuid PK——`PUT/DELETE /products/{id}/plans/{plan_id}` 的位址。 */
  id: string
  key: string
  label: string
  /** `plan`（可販售）｜`trial`（試用模板，不進套裝清單）｜`addon`（加購模板）。 */
  kind: 'plan' | 'trial' | 'addon' | string
  sort_order: number
  is_active: boolean
  seats: number | null
  term_days: number | null
  /** 批108：期限的分鐘餘數（0–1439）。套裝期限＝`term_days * 1440 + term_minutes`
   *  ——「2 小時體驗」這種模板 `term_days` 是 **0**，判斷有沒有期限一律比總和。 */
  term_minutes: number
  max_product_lines: number | null
  modules: Record<string, string>
  quotas: Record<string, number | null> | null
  /**
   * 批110：這個套裝賣出去時 payload 的 `policy` **預設值**（與 `quotas` 的角色對稱）。
   * #5494「各套裝不同寬限期」就是靠它——`grace_days` 只是 CortexDC 自己宣告的一個 policy 鍵。
   */
  policies?: Record<string, unknown>
  /** 可免審直接核發的**最低身分**：superadmin｜manager｜admin｜reseller（批93-C 立、批110 加一級）。 */
  activation_policy: string
  trial: boolean
  /**
   * 批155：套裝層的「平台自動核准」開關（只對 `kind='plan'` 有意義，其他 kind 寫 true 一律 422
   * `auto_approve_not_applicable`）。要與平台層的開關**同時**打開才生效。只有營運主管以上能改。
   */
  auto_approve?: boolean
  /**
   * 批155：內部套裝——任何投影都不列、任何外部申請路徑都不收；後台只有營運主管以上能拿它
   * 核發或換證。只有營運主管以上能改。
   */
  internal?: boolean
  /**
   * 批155：子證模板（`kind='addon'`）的上限宣告：`{配額鍵: {max?, max_delta?}}`。
   * **有宣告的鍵＝平台可以填的鍵**；`null`＝平台不能為這個模板填任何配額、只能照模板。
   */
  addon_limits_json?: AddonLimits | null
  /** 批155：子證模板「跟隨主證到期」（簽發時到期日＝現行主證的到期日，主證換發時連帶換發）。 */
  follow_main_expiry?: boolean
}

/** 子證模板一個配額鍵的上限：`max`＝單張的值、`max_delta`＝有取代對象時「新值 − 舊值」。 */
export interface AddonLimit {
  max?: number | null
  max_delta?: number | null
}
/** 批155：`product_plans.addon_limits_json` 的形狀（鍵＝產品已宣告的配額鍵）。 */
export type AddonLimits = Record<string, AddonLimit>

/** 產品層級選項（逐鍵見 `docs/08-DB-SCHEMA-LS.md` 的 `products.options_json`）。
 *  取值一律走 `lib/products.ts::optionOf`——後台建的產品可能沒填滿每一個鍵。 */
export type ProductOptionsMap = Record<string, unknown>

export interface ProductSpec {
  id: string
  key: string
  name: string
  /** active｜deprecated（停售：核發下拉不再出現，歷史授權照樣查得到）。 */
  status: string
  signing_kid: string | null
  modules: ModuleSpec[]
  quotas: QuotaSpec[]
  /** 批109：行為參數的憑證設計。CS 目前是空陣列（格式先到位、鍵集之後由營運決定）。 */
  policies: PolicySpec[]
  options: ProductOptionsMap
  notes: string | null
  plans: PlanSpec[]
}

/** 全部產品（含已停售）＋各自的憑證設計與套裝。後台〈產品〉頁與核發表單的單一來源。 */
export const listProducts = () => api<ProductSpec[]>('/api/admin/products')
export const getProduct = (key: string) =>
  api<ProductSpec>(`/api/admin/products/${encodeURIComponent(key)}`)

/** 建一個空產品（憑證設計全空）；實務上多半改用「以此為樣板」（clone）。superadmin。 */
export const createProduct = (body: {
  key: string
  name: string
  signing_kid?: string | null
  notes?: string | null
}) => api<ProductSpec>('/api/admin/products', { method: 'POST', body })

/** 更新產品基本資料。**沒有 `key`**——它被逐字寫進每一張已簽發授權的 payload。superadmin。 */
export const updateProduct = (
  id: string,
  body: { name: string; status: string; signing_kid?: string | null; notes?: string | null },
) => api<ProductSpec>(`/api/admin/products/${id}`, { method: 'PUT', body })

/** 以既有產品為樣板建立新產品（憑證設計整份複製，可選是否連套裝）。superadmin。 */
export const cloneProduct = (
  id: string,
  body: { key: string; name: string; include_plans?: boolean },
) => api<ProductSpec>(`/api/admin/products/${id}/clone`, { method: 'POST', body })

/**
 * 整包覆寫憑證設計（模組／配額／產品選項）。superadmin。
 * ⚠️ **只影響之後核發的證**；既有授權是簽好的快照，不會因此變動。
 */
export const updateProductSchema = (
  id: string,
  body: {
    modules: Record<string, unknown>[]
    quotas: Record<string, unknown>[]
    /** 批109：行為參數。後端預設空陣列，舊前端不送不會清空既有設計。 */
    policies: Record<string, unknown>[]
    options: ProductOptionsMap
  },
) => api<ProductSpecWithWarnings>(`/api/admin/products/${id}/schema`, { method: 'PUT', body })

/**
 * 產品寫入端點的回應（批141）：產品本體＋**不擋存檔的提醒碼**。
 *
 * 目前只有 `hosting_profile_missing`＝「開放由我方代管」是開的，但沒有有效的代管設定
 * ——其實開不了站（後端開站一律 422 `hosting_profile_missing`）。
 */
export type ProductSpecWithWarnings = ProductSpec & { warnings?: string[] }

/**
 * 設定（整份取代）產品的**代管設定**（`options.hosting_profile`，批141）。manager 以上。
 *
 * ★ 嚴格驗證：失敗 422，`detail` 形狀固定 `{code, field, message}`
 *   （`code` ∈ invalid_type／unknown_key／missing_key／invalid_value；`field` 是點分路徑）
 *   ——前端據此把紅字標到那一格（`lib/hostingProfile.ts::asProfileIssue`）。
 * ⚠️ 不經 `PUT …/schema`：那一支**忽略** body 裡的 `hosting_profile` 並保留 DB 現值。
 */
export const putHostingProfile = (id: string, profile: Record<string, unknown>) =>
  api<ProductSpecWithWarnings>(`/api/admin/products/${id}/hosting-profile`, {
    method: 'PUT',
    body: profile,
  })

/**
 * 移除產品的代管設定。manager 以上。還開放代管（`supports_hosting`）時 409
 * `hosting_enabled`——先到〈憑證設計〉關掉「開放由我方代管」再刪。沒有設定時冪等 200。
 */
export const deleteHostingProfile = (id: string) =>
  api<ProductSpecWithWarnings>(`/api/admin/products/${id}/hosting-profile`, { method: 'DELETE' })

/** 套裝新增／修改的 body（形狀與後端 `PlanIn` 一致）。 */
export interface PlanInput {
  key: string
  label: string
  kind: string
  sort_order: number
  is_active: boolean
  /**
   * @deprecated 批114：後台**不再送**這兩個相容欄——套裝的數字一律填在 `quotas` 裡。
   * 後端 `PlanIn` 仍受理（有值時由 `_plan_quotas` 折進 `quotas_json`），所以型別留著，
   * 但新的呼叫端不要用；「鍵不放」＝由操作者核發時自己填，靠的就是 `quotas` 少那個鍵。
   */
  seats?: number | null
  term_days: number | null
  /** 批108：期限的分鐘餘數（0–1439）。套裝期限＝`term_days * 1440 + term_minutes`。 */
  term_minutes: number
  /** @deprecated 同 `seats`（批114）。 */
  max_product_lines?: number | null
  modules: Record<string, string>
  quotas: Record<string, number | null> | null
  /** 批110：這個套裝的行為參數**預設值**（#5494 的「各套裝不同寬限期」）。 */
  policies: Record<string, unknown>
  activation_policy: string
  trial: boolean
  /** 批155（營運主管以上）：套裝層的平台自動核准開關。只對 `kind='plan'` 有意義。 */
  auto_approve?: boolean
  /** 批155（營運主管以上）：內部套裝。 */
  internal?: boolean
  /** 批155：子證模板的上限宣告（只用在 `kind='addon'`；null＝不開放平台填配額）。 */
  addon_limits_json?: AddonLimits | null
  /** 批155：子證模板跟隨主證到期（只用在 `kind='addon'`）。 */
  follow_main_expiry?: boolean
}
export const createPlan =(productId: string, body: PlanInput) =>
  api<PlanSpec>(`/api/admin/products/${productId}/plans`, { method: 'POST', body })
export const updatePlan = (productId: string, planId: string, body: PlanInput) =>
  api<PlanSpec>(`/api/admin/products/${productId}/plans/${planId}`, { method: 'PUT', body })
/** 只在**零引用**時可刪，否則 409 `plan_in_use`（導向改成「停用」）。 */
/**
 * 刪套裝（批110 起可 `force`）。零引用直接刪；有授權／申請引用時預設 409 `plan_in_use`，
 * 帶 `force` 才真的刪（代價：舊證的「套裝」欄查不到內容了，稽核留內容快照）。
 */
export const deletePlan = (productId: string, planId: string, force = false) =>
  api<null>(
    `/api/admin/products/${productId}/plans/${planId}${force ? '?force=true' : ''}`,
    { method: 'DELETE' },
  )

/** 刪產品前的確認資料（批110）：六張弱關聯表的引用筆數 ＋ 會被清掉的範圖。 */
export interface ProductDeletionPreview {
  key: string
  name: string
  references: Record<string, number>
  total_references: number
  /** 零引用＝可以直接刪；有引用要 `force`（UI 另外要求打字輸入 key）。 */
  deletable: boolean
  plans: number
  /**
   * 批151：會連帶刪除的產品參數版本數（與套裝同性質：產品自己的資料，不算引用、不擋刪除，
   * 所以不在 `references` 裡）。舊版後端沒有這個鍵＝視為 0。
   */
  param_versions?: number
  scope: { admin_users: number; resellers: number; scope_emptied: number }
}

export const productDeletionPreview = (id: string) =>
  api<ProductDeletionPreview>(`/api/admin/products/${id}/deletion-preview`)

/**
 * 刪產品（批110）。★ 產品 key 逐字寫進每一張已簽發憑證的簽章明文——刪掉之後那些證的
 * 模組／配額鍵就再也翻不回中文。所以有引用時一定要 `force`，而且 UI 會要求打字輸入 key。
 */
export const deleteProduct = (id: string, force = false) =>
  api<null>(`/api/admin/products/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' })

export interface Customer {
  id: string
  name: string
  domain: string | null
  notes: string | null
  created_at: string
  /**
   * 批93-C：這個客戶的歸屬代理商；**null＝直營**（我方自己的客戶）。
   * 一個客戶只能掛一個代理商或不掛（單一 nullable FK，刻意不做多對多）。
   */
  reseller_id?: string | null
  reseller_name?: string | null
  /** 批81 需求 11：`company`／`personal`；null＝批81 之前的舊客戶，還沒補填。 */
  kind: string | null
  /** **首次**完成基本資料填寫的時刻；null＝還沒填（portal 會強制他填）。 */
  profile_completed_at: string | null
  /** 用他的客戶碼帶進來的客戶數（清單與詳情都帶）。 */
  referral_count?: number
  /**
   * 批141：來源平台——這個客戶是某個多租戶平台的組織（**含已解除關聯的**）。
   * null＝直營／代理商客戶。平台客戶不可刪除、合併，也不給 portal（不能邀請成員）。
   */
  platform?: PlatformRef | null
}

/**
 * 客戶／申請投影上的「來源平台」（批141；後端 `_projections.platform_by_customer`）。
 * `detached_at` 非 null＝已解除關聯（仍然是平台來的客戶：一生只能屬於那一個平台組織）。
 */
export interface PlatformRef {
  id: string
  key: string
  name: string
  external_ref: string
  detached_at?: string | null
}

/** 客戶基本資料（批81 需求 11）——形狀與 portal `GET /me` 的 `profile` 一致，多帶中文 label。 */
export interface CustomerProfile {
  kind: string | null
  contact_name: string | null
  phone: string | null
  company_name: string | null
  tax_id: string | null
  /**
   * 批97 Bug 7：我方／代理商**代填**的兩欄（`customers` 上的開欄，不在 `profile_json` 裡）。
   * 客戶自助表單刻意不收——portal 的 `PUT /profile` 對 `profile_json` 是整包重指派，
   * 塞進那包會被客戶下次儲存靜默清空。
   */
  contact_email: string | null
  address: string | null
  title: string | null
  line_id: string | null
  industry: string | null
  industry_label: string | null
  heard_from: string | null
  heard_from_label: string | null
  heard_from_other: string | null
  company_size: string | null
  /**
   * 批114（裁示 ㉘）：客戶自填的**預計規模**（原本叫 `expected_seats`，是 CS 一個產品的
   * 欄位名）。後端 `0032` 把 `profile_json` 的鍵改名，輸入仍相容舊鍵、輸出只給新鍵。
   */
  expected_scale: number | null
  /**
   * 批114（裁示 ㉜）：客戶在〈組織資料〉選的**主要產品**（產品 key；沒選＝null）。
   * 之後申請授權／代管時預設帶入這個產品，申請當下仍可改選。
   */
  primary_product: string | null
  /** 主要產品的中文名（產品被刪／停售時可能是 null）。 */
  primary_product_name: string | null
  website: string | null
  city: string | null
  needs: string | null
  marketing_opt_in: boolean
  terms_accepted_at: string | null
  terms_version: string | null
  profile_completed_at: string | null
}

/** 邀請關係：客戶碼帶 `customer_id`／`customer_name`（做成連結）；業務／活動碼只有 `label`。 */
export interface ReferredBy {
  code: string
  kind: string
  label: string | null
  customer_id: string | null
  customer_name: string | null
}

export interface CustomerDetail extends Customer {
  profile: CustomerProfile
  own_code: { code: string; is_active: boolean } | null
  referred_by: ReferredBy | null
  referrals: { customer_id: string; customer_name: string; created_at: string }[]
  referral_count: number
}

/** 邀請碼（批81 需求 11 裁示 17）。`kind`＝customer｜sales｜campaign。 */
export interface InviteCodeRow {
  id: string
  code: string
  kind: string
  label: string | null
  note: string | null
  is_active: boolean
  customer_id: string | null
  customer_name: string | null
  referral_count: number
  created_by: string | null
  created_at: string
}

/** 部署心跳回報的使用量快照（CS 每小時回報；`GET /licenses` 只配對部署目前回報的那張證）。 */
export interface LicenseUsage {
  seats_in_use: number
  partner_seats_in_use: number
  /** 九個 max_* 的使用量；舊版 CS 不帶＝null。 */
  quotas_in_use: Record<string, number> | null
  last_seen: string
}

export interface License {
  license_id: string
  customer_id: string
  /** 批93 多產品：這張證屬於哪個產品（`products.key`，逐字寫進簽章明文）。 */
  product: string
  status: string
  seats: number
  modules: Record<string, string>
  /** 產品線數量上限；null＝不限。 */
  max_product_lines: number | null
  /** 批65：授權套裝名快照（lite|standard|professional|enterprise）；null＝客製。 */
  plan: string | null
  /** 批65：數量配額（partner_seats＋九個 max_*）；值 null＝無上限、鍵缺席/整包 null＝不限。 */
  quotas: Record<string, number | null> | null
  /** 批110：行為參數（payload 概念，沒有 DB 欄）。詳情 modal 與換發的起手值。 */
  policy?: Record<string, unknown> | null
  /**
   * 批110：延後生效（`null`＝立刻生效）。清單的「尚未生效」徽章讀它。
   * ★ CS 端收到這種證時會**先收著繼續用舊的**，不會進唯讀
   *   （`services/license._should_defer`）。
   */
  not_before?: string | null
  expires_at: string | null
  /**
   * 期限（批108）：總長度＝`term_days * 1440 + term_minutes`。
   *
   * ★ 判斷「有沒有期限」請用 `lib/terms.hasTerm`——分鐘級的臨時憑證 `term_days` 是 **0**
   *   （falsy），`if (lic.term_days)` 會把它看成「沒有期限」。
   */
  term_days?: number | null
  term_minutes?: number
  deployment_mode: string
  max_deployments: number
  machine_fingerprint: Record<string, string> | null
  host_instance_id: string | null
  /**
   * 主機顯示名，三來源回退：客戶自訂 label > CS 心跳 hostname > **代管站 slug**
   * （第三個是批80 批B3 補的——代管站沒有 DeploymentLink、CS 首跳前沒有 hostname，
   * 在那之前這欄恆為 null，開站當下最需要看的時候什麼都看不到）。
   */
  instance_name?: string | null
  /**
   * 代管維度（批80 批B3；只有 `GET /licenses` 帶）。null＝自部署。
   * **沒有 `is_managed` 布林**——`managed_host_id != null` 就是它。
   */
  managed_host_id?: string | null
  managed_host_name?: string | null
  /** 只有 `GET /licenses` 帶；null＝這張證從未被部署回報（尚未回報／離線授權）。 */
  usage?: LicenseUsage | null
  /**
   * 批93-B：這張證**所在主機**目前有幾張有效加購（`GET /licenses` 才帶）。
   * ★ 同一台上的多張證會看到同一個數字——加購綁的是主機不是證，那是對的。
   */
  addon_count?: number
  /**
   * 批93-B：**有效** entitlement＝主證 ＋ 目前生效的加購（`GET /licenses` 才帶）。
   *
   * ★★ 顯示配額／模組一律用這兩欄，**不要用 `lic.quotas`／`lic.modules`**：
   *   只看主證的話，「主證 300 篇＋加購到 20000 篇」的站台會被畫成
   *   `1,918 / 300` 紅字＝假警報（E2E `b-e2e-04` 實例）。
   *   沒有任何加購時它們逐位元等於主證的值，所以不必寫 `?? lic.quotas`。
   */
  effective_quotas?: Record<string, number | null> | null
  effective_modules?: Record<string, string>
  /**
   * 哪些鍵是被加購**提升**的 → `{key: 最早到期 iso | null}`（null＝那張還沒蓋章）。
   * 清單用它畫「含加購」小標與 tooltip。
   */
  addon_sources?: {
    quotas: Record<string, string | null>
    modules: Record<string, string | null>
  }
  supersedes: string | null
  /**
   * 批93-C：簽發當下的經手代理商快照（**歷史值不隨客戶改派而變**）；null＝直營。
   * 續約與付費變更以「當下 `customers.reseller_id`」為準，同內容重簽才沿用快照。
   */
  reseller_id?: string | null
  reseller_name?: string | null
  /**
   * 批93-C：這張證是怎麼出來的——`request`（走申請審核）｜`policy`（套裝政策允許免審核發）
   * ｜**`direct`（superadmin 越權直發＝break-glass，畫面標「直發」並另發告警）**｜
   * `system`（換機／transfer／合併／補鍵等同內容重簽）。舊列＝undefined。
   * ★ `direct` **不會**傳染到續約——每次簽發當下重新決定（`_carry_over` 刻意不帶它）。
   */
  issued_via?: string | null
  /**
   * 簽章原文。★ 批93-D 起**清單端點不再回這一欄**（一頁 50 張＝50 份可安裝的授權明文
   * 進瀏覽器快取與 devtools）——只有單張回應（簽發／續期／撤銷）還帶。所以型別是選填，
   * 清單畫面請不要讀它。
   */
  blob?: string
  created_at: string
}

/**
 * 授權清單的一頁（批93-D §2.10／效能審計 R1）。
 *
 * LS 沒有 Redis，用不了 CS 那套 id 快照分頁 → keyset：`next_cursor` 非 null＝還有下一頁。
 * ★ 游標是**不透明字串**——別解析它（內容是 `(created_at, id)`，但那是後端的排序鍵，
 *   哪天換了排序，解析過它的呼叫端就全壞）。
 */
export interface LicensePage {
  items: License[]
  next_cursor: string | null
}

/**
 * 批93-D（LS-4）：其餘五張後台清單也改成同一套 keyset 信封。
 *
 * 形狀與 `LicensePage` 一字不差（`{items, next_cursor}`），游標同樣是**不透明字串**
 * ——別解析它。`next_cursor` 非 null＝還有下一頁；一頁預設 50 筆、上限 200。
 *
 * ★ 措辭紀律：LS 目前只有 2 個客戶／2 筆部署，這**現在完全不痛**。改的理由是「還沒有
 *   任何呼叫端依賴『一次拿全部』」——等有人依賴了才改，就要同時動 API 契約與前端。
 */
export interface CustomerPage {
  items: Customer[]
  next_cursor: string | null
}
export interface DeploymentPage {
  items: Deployment[]
  next_cursor: string | null
}
export interface LicenseReqPage {
  items: LicenseReq[]
  next_cursor: string | null
}
export interface RebindRequestPage {
  items: RebindRequest[]
  next_cursor: string | null
}
export interface InviteCodePage {
  items: InviteCodeRow[]
  next_cursor: string | null
}

export interface Host {
  instance_id: string
  label: string | null
  hostname: string | null
  last_seen: string | null
  conflict_flag: boolean
  /** 心跳回報的使用量；無部署列＝0／0／null。 */
  seats_in_use: number
  partner_seats_in_use: number
  quotas_in_use: Record<string, number> | null
  license: License | null
  /**
   * 批80 批D1：這台是**我方代管**的站（`host_sites` 有在役列）。代管站的維運責任在我方，
   * 客戶動不了、後台也不提供逐站轉移（後端 `transfer_host` 對代管站回 403）——換機一律走
   * 代管主機頁的「換機：整批重簽授權」。
   */
  hosted?: boolean
  /** 代管站的對外事實（客戶自己就看得到的網址）；非代管＝null。 */
  site?: { domain: string; public_url: string | null } | null
  /** 批93-B：這台目前有效的加購（**不帶 blob**；主機列的 chips 與「加購」鈕讀它）。 */
  addons?: LicenseAddon[]
}

export interface CustomerHosts {
  hosts: Host[]
  offline_licenses: License[]
}

export interface Deployment {
  instance_id: string
  instance_name?: string | null
  license_id: string
  /** 經授權推回的客戶（孤兒部署＝null）。 */
  customer_id: string | null
  customer_name: string | null
  /**
   * 批93 多產品：這筆部署跑的那張證是哪個產品的（由 `license_id` 推回）。
   * **null＝孤兒部署**（證被刪了）→ 拿不到憑證設計，配額鍵退化成原始英文字串。
   */
  product: string | null
  /**
   * 批107：這台回報的那張證的**代理商快照**（null＝直營，或孤兒部署）。
   * 與代理商過濾器同一個來源——不會出現「用 A 過濾卻顯示 B」。
   */
  reseller_id?: string | null
  reseller_name?: string | null
  /**
   * 代管維度（批80 批B2）。null＝自部署——部署頁的「型態」分頁籤就靠這一欄分桶，
   * **不另立 `is_managed`**（兩個欄位表達同一件事遲早不一致）。
   */
  managed_host_id: string | null
  managed_host_name: string | null
  /**
   * 批171：多租戶平台（SaaS）維度——這台是哪個平台旗下的組織（含已解除關聯的）。
   * null＝不是平台組織（代管或自部署）。部署頁的「SaaS」分頁靠它分桶。舊版 LS 沒有這兩欄＝undefined。
   */
  platform_id?: string | null
  platform_name?: string | null
  app_version: string | null
  hostname: string | null
  /**
   * 對外 IP（LS 由心跳請求來源記錄）。後端從批79 就回了、前端一直沒用的舊債，
   * 批80 批B2 補進部署頁表格。
   */
  last_ip: string | null
  /** OS 等主機資訊；要等 CS 端上報（批C2）才有值，先留欄位。 */
  host_info: Record<string, unknown> | null
  seats_in_use: number
  partner_seats_in_use: number
  quotas_in_use: Record<string, number> | null
  /**
   * 這筆部署當下跑的那張證的**上限**（批81 需求 6）——部署列本身只有「已用」，
   * 要畫「已用 / 上限」的橫條就得有這個。null＝找不到對應授權（孤兒部署）。
   * `quotas` 為 null 或缺鍵的語意見 `lib/quotas.ts::quotaLimit`。
   */
  limits: {
    seats: number
    plan: string | null
    /** **主證**的配額（契約不變）。 */
    quotas: Record<string, number | null> | null
    /**
     * 批169：主證＋生效中的加購子證，依每個鍵宣告的合併方式算好的結果（無子證時逐位元等於
     * `quotas`）。畫「已用 / 上限」一律用這一份——容量加購（相加）只看主證會畫出假紅字。
     * 舊版 LS 沒有這一欄 → 呼叫端退回 `quotas`。
     */
    effective_quotas?: Record<string, number | null> | null
    /** 批169：被子證改變的配額鍵 → 貢獻子證中最早的到期時刻（null＝還沒蓋章）。 */
    addon_sources?: Record<string, string | null>
  } | null
  conflict_flag: boolean
  /**
   * 批93 §2.4：這台**最後一次通過心跳驗章**的時刻（heartbeat body 帶 `license_blob`、
   * LS 驗過簽章且 license_id 相符）。null＝從未認證（舊版 CS 還沒升級）——`degrade`
   * 模式下那些心跳不寫任何欄位，部署頁的「未認證」徽章就是在說明這件事。
   */
  blob_verified_at?: string | null
  /**
   * 批112：產品回報的**站台指標**快照（泛型 `{key: number}`；LS 不解讀語意）。
   * null／缺席＝這台從來沒送過（舊版產品端）。顯示名／單位／格式查 `metricDefs`
   * （`lib/metrics.ts`），查不到就原樣印英文鍵。
   */
  metrics?: Record<string, number> | null
  /** 上面那份快照的時刻。與 `last_seen` **不同**——站台可能持續心跳但不送指標。 */
  metrics_at?: string | null
  last_seen: string
}

export interface RebindRequest {
  id: string
  customer_id: string | null
  old_license_id: string | null
  new_fingerprint: string | null
  status: string
  issued_license_id: string | null
  created_at: string
}

export interface CustomerUser {
  id: string
  email: string
  active: boolean
}

export interface LicenseReq {
  id: string
  customer_id: string
  /** 批93 多產品：這筆申請要的是哪個產品（核發框依此載憑證設計；畫面唯讀）。 */
  product: string
  requested_by: string | null
  machine_fingerprint: Record<string, string> | null
  host_instance_id: string | null
  instance_name: string | null
  note: string | null
  seats_requested: number | null
  /** 批65：portal 申請時選的**套裝**（lite|…）；⚠️ 與 request_json.requested.plan 不同——
   *  那個是歷史命名的「期間方案」（monthly|annual）。 */
  plan?: string | null
  quotas_requested?: Record<string, number | null> | null
  /** 批93-C：申請內容裡指定的模組（我方／代理商草稿改過內容後由後端投影上來）。 */
  modules_requested?: Record<string, string> | null
  /** 批93-C：申請內容裡指定的期限天數；null＝沿用產品預設（`change` 則沿用現行證）。 */
  term_days?: number | null
  /**
   * 批108：申請內容裡指定的期限分鐘餘數（0–1439）。**總期限＝`term_days * 1440 +
   * term_minutes`**——判斷「有沒有指定期限」要看兩欄，分鐘級的內容 `term_days` 是 0。
   */
  term_minutes?: number | null
  /** 批93-C：申請內容裡指定的產品線上限；null＝未指定（`change` 沿用現行證）。 */
  max_product_lines?: number | null
  /** 申請內容裡指定的部署模式（`online`｜`airgapped`）；null＝未指定。 */
  deployment_mode?: string | null
  /**
   * ★★ 以下三個是批113 新增（#5520／#5521）：申請內容裡指定的**行為參數**與
   * **啟用／到期時刻**。在這之前申請路徑整條沒有它們，草稿裡填的東西會被靜默丟掉。
   *
   * ★ 後端只有 `origin='admin'`（我方草稿）的列讀得出值——客戶／代理商送件路徑
   *   原樣落庫不經白名單，讀端閘在 `services/license_request.requested_content()`。
   *   所以代理商頁面裡這三個一律 `null`，不要拿來顯示。
   */
  policy?: Record<string, unknown> | null
  /** 申請內容裡指定的延後生效時刻（ISO；null＝未指定＝立刻生效）。 */
  not_before?: string | null
  /** 申請內容裡指定的到期時刻（ISO；null＝未指定＝依期限起算）。有值時核發會把 term 清掉。 */
  expires_at?: string | null
  /** 清單端點批載後帶上的客戶名（免前端再查一次 `customers`）。 */
  customer_name?: string | null
  // 客戶申請內容（requested.{seats,plan,modules}）+ instance_id 等
  request_json: Record<string, any> | null
  /**
   * **draft**（批93-C 新增：內容可改，還沒送出）｜pending（已確認送出、等開通）｜
   * approved（已核發＝已簽發）｜rejected（我方婉拒）｜cancelled（maker 自己撤回）。
   *
   * ★ 「已退回」**不是**狀態——`return` 把 pending 打回 draft 並在 `review.reason`
   *   留下理由，所以畫面上是「草稿頁籤裡 `review.reason` 非空」的子集合。
   */
  status: string
  /**
   * 批93-C：誰送的。`portal`／`site`＝客戶自助、`reseller`＝代理商、`admin`＝我方代客建立。
   * 舊列缺席＝視同 portal。**與 `kind` 正交**（kind 是內容形態，origin 是來源）。
   */
  origin?: string | null
  /** 送件當下的代理商快照（`origin=reseller` 才有值）。 */
  reseller_id?: string | null
  reseller_name?: string | null
  /** 批141：申請人所屬的多租戶平台（null＝不是平台客戶）。`origin=platform`＝平台代送。 */
  platform?: PlatformRef | null
  /** 確認鎖定（draft → pending）的時刻。 */
  confirmed_at?: string | null
  /** 開通／婉拒（pending → approved｜rejected）的時刻。 */
  reviewed_at?: string | null
  /**
   * ★★ 以下三個是 **staff realm 專屬**（`/api/admin/license-requests`）。
   * 代理商 realm（`/api/admin/reseller/license-requests`）的投影**完全不帶**它們
   * ——主人裁示：代理商的列不得出現任何我方員工 email（`_projections.py::
   * license_request_out(include_staff_fields=False)`，連 `request_json.review.by`
   * 都清掉）。所以在代理商頁面裡讀這三個欄位一律是 `undefined`，不要拿來顯示。
   */
  confirmed_by?: string | null
  reviewed_by?: string | null
  /** 開通備註（**內部**，客戶／代理商都看不到；他們看得到的是 `review.reason`）。 */
  review_note?: string | null
  /**
   * 送出當下的核發政策快照（superadmin｜admin｜reseller＝可免審直接核發的最低身分）。
   * 退回草稿時後端會清空，再次確認時重算；`approve` 取「快照與現行政策的較嚴者」。
   */
  activation_policy?: string | null
  /**
   * 客戶／代理商看得到的審核／退回理由（`request_json.review` 的投影）。
   * `by` 只在 staff realm 有值——代理商投影會把它拿掉（見上）。
   */
  review?: { reason?: string | null; at?: string | null; by?: string | null } | null
  issued_license_id: string | null
  /**
   * 批155：這筆申請是怎麼核發的——`platform`＝合作平台送件、雙層開關都開且內容逐字等於模板，
   * **當場自動核准**（`reviewed_by` 留 null）。其餘值沿用授權的 `issued_via` 語彙；
   * 舊後端不回這一欄＝undefined。
   */
  issued_via?: string | null
  /**
   * 批155：平台送件**沒有**自動核准時的原因（`auto_approve_off`／`internal_plan`／
   * `content_differs`／`term_not_allowed`／`renew_too_early`）；中文對照走
   * `lib/autoApprove.ts::reviewReasonLabel`。只有平台來源的申請才可能有值。
   * ★ 與 `review.reason`（我方退回／婉拒時寫給送件方看的理由）是兩件事，只是撞字。
   */
  review_reason?: string | null
  /**
   * 批108：加購（`kind="addon"`）核發出來的**子證** id——與 `issued_license_id` 互斥
   * （加購核發時那一欄是 null）。申請詳情的「處理結果」靠這兩個之一畫出取證按鈕。
   */
  issued_addon_id?: string | null
  created_at: string
  /**
   * 批81：站台**第一次**回報這張核發出來的證的時刻（一次性事件，之後換證也不改）。
   * null＝批81 之前的舊列或尚未生效。
   */
  applied_at?: string | null
  /** 批81：核發出來那張證**現在**的狀態；`missing`＝有 id 但查無列（人工刪過）。 */
  license_status?: 'active' | 'superseded' | 'revoked' | 'missing' | null
  /** 批80 批D2：該主機由我方代管 → 核發時 `deployment_mode` 硬鎖 online（撤銷唯一通道）。 */
  hosted?: boolean
  /**
   * 申請內容形態：`new`＝新證（帶 request.json）、`change`＝變更既有主機的授權、
   * **`renew`**＝續約、**`addon`**＝加購子憑證（批93-C 兩者新增）、
   * `hosted_provision`＝自助申請代管開站（走另一個核可對話框）。
   */
  kind?: 'new' | 'change' | 'renew' | 'addon' | 'hosted_provision' | string
  /**
   * 批80 批D2：該主機**現行** active 授權的摘要，核發框的「現行 vs 申請」差異表基底。
   * ⚠️ **不含 `blob`**（列表一次三十筆，夾 blob 等於把三十張授權明文送進瀏覽器）。
   * null＝該主機沒有現行授權，或現行那張屬於別的客戶（弱關聯撞號）。
   */
  current?: LicenseBrief | null
}

/** 授權摘要（`LicenseReq.current`）——`License` 的子集，刻意不帶 blob。 */
export interface LicenseBrief {
  license_id: string
  /** 批93 多產品；變更申請沿用現行證的產品（不收客戶輸入）。 */
  product?: string
  status: string
  seats: number
  modules: Record<string, string>
  max_product_lines: number | null
  plan: string | null
  quotas: Record<string, number | null> | null
  /**
   * 批113：行為參數。**摘要從此也回 `policy`**（`_projections.license_brief`）——
   * 在這之前 policy 只住 `payload`，換發 modal 與詳情 modal 的起手值恆空，
   * 畫面會拿產品的 `absent_default` 頂替（證上寬限 30 天卻顯示 14，比空白更糟）。
   * ★ 代理商讀得到但**不能指定**（送出去一律 422 `field_not_allowed`），與 `not_before` 現況一致。
   */
  policy?: Record<string, unknown> | null
  expires_at: string | null
  /**
   * 批110：延後生效（`null`＝立刻生效）。清單的「尚未生效」徽章讀它。
   * ★ 代理商讀得到但**不能指定**（換發送這個欄位一律 422 `field_not_allowed`）。
   */
  not_before?: string | null
  term_days: number | null
  /** 批108：期限的分鐘餘數（0–1439）。總期限＝`term_days * 1440 + term_minutes`。 */
  term_minutes: number
  deployment_mode: string
  machine_fingerprint: Record<string, string> | null
  host_instance_id: string | null
  created_at: string
}

export interface AdminUserRow {
  id: string
  email: string
  /**
   * operator＝維運人員（只看代管主機／告警／部署，見 `stores/auth.ts` 的 ROLE_RANK）；
   * **manager＝營運主管**（批110，介於 admin 與 superadmin：授權客製、兌換活動、代理商
   * 公司與窗口帳號、客戶刪除合併、稽核唯讀、產品憑證設計）；
   * **reseller＝外部代理商**（批93-C；不在階序內，只進 `/api/admin/reseller/*`）。
   */
  role: 'superadmin' | 'manager' | 'admin' | 'operator' | 'reseller'
  /** 批93-C：所屬代理商公司。不變式 `role==='reseller'` ⇔ 此欄非 null。 */
  reseller_id?: string | null
  reseller_name?: string | null
  /** 批93-C：這個帳號的產品範圖（`products.key`）；**null＝不限**。任何角色都可限。 */
  product_keys?: string[] | null
  active: boolean
  created_at: string
  /** 顯示名稱（首登時從 Google 回填，只補空值；後台可手動改）。 */
  name: string | null
  last_login_at: string | null
  /** 是否登入過（預建但未首登＝false）。後端刻意不回 google_sub 本身（個資）；
   *  判準＝`google_sub` 有值 **或** 有 `last_login_at`（dev-login 不帶 Google sub）。 */
  has_logged_in: boolean
  /** 強制登出界線：早於此刻簽出的 token 一律 401。null＝從未強制登出。 */
  token_not_before: string | null
  /** 預建這個帳號的員工 email；自行登入建立的為 null。 */
  invited_by: string | null
}

/** 一筆後台操作稽核。`actor_email` 為 null＝系統動作。 */
export interface AuditRow {
  id: string
  actor_email: string | null
  action: string
  /** 後端查表給的中文動作名（查不到時等於 action 本身）。 */
  action_label: string
  target_type: string | null
  target_id: string | null
  summary: string
  detail: Record<string, unknown> | null
  /** 來源 IP：欄位已建好但**本批一律 null**（等 nginx 轉發鏈確定）。 */
  ip: string | null
  created_at: string
}

/** keyset 游標：上一頁最後一列的 `(created_at, id)`；`next` 為 null＝沒有下一頁。 */
export interface AuditCursor {
  at: string
  id: string
}
export interface AuditPage {
  rows: AuditRow[]
  next: AuditCursor | null
}

export interface IssueInput {
  /**
   * 批93 多產品：**必填**。這個字串會逐字寫進授權明文的 `product`，產品端硬檢它——
   * 填錯的證會被對方拒絕，症狀只有「客戶說授權無效」。
   */
  product: string
  modules: Record<string, string>
  /** 批65：套裝名快照（顯示用；手調過數量仍保留原名）。 */
  plan?: string | null
  /**
   * 批65：數量配額。**批114 起所有數量都在這裡**——後端仍收得下舊的一級數字欄
   * （相容入口，見 `services/quota_compat.py`），但後台不再送，避免同一個數字有兩個
   * 輸入框、獨立欄靜默蓋掉配額裡填的值。值 `null`＝無上限；鍵缺席＝依憑證設計的
   * `absent_default`。必填／可否無上限由憑證設計的旗標決定（缺鍵 → 422 `quotas_invalid`）。
   */
  quotas?: Record<string, number | null> | null
  /**
   * 批110：行為參數（#5494）。`null`／省略＝從套裝模板展開；顯式給就以這份為準，
   * 值域依產品憑證設計的宣告校驗（超出 → 422 `policy_invalid`）。
   */
  policy?: Record<string, unknown> | null
  /** 批110（#5496）：延後生效的 ISO 時刻；`null`＝立刻生效。 */
  not_before?: string | null
  /** 批110（#5492）：`on_fetch`＝首次取證才起算（預設）／`at`＝指定到期時刻。 */
  expiry_mode?: 'on_fetch' | 'at'
  /** `expiry_mode="at"` 時的到期時刻（ISO）。 */
  expires_at?: string | null
  term_days: number
  /**
   * 批108：期限的分鐘餘數（0–1439）。總期限＝`term_days * 1440 + term_minutes`；
   * 後端擋「兩欄都是 0」（那會簽出一張永不到期的證）。
   */
  term_minutes?: number
  deployment_mode: string
  max_deployments?: number
  machine_fingerprint?: Record<string, string> | null
  host_instance_id?: string | null
}

/** 核發 / 核准回應附帶的警告：授權裡不在 LS 已知清單內的模組 key（打錯字＝該模組直接停用）。 */
export interface UnknownModulesWarning {
  unknown_modules?: string[]
  unknown_quota_keys?: string[]
  /** 批93：scoped 模組的子項值不在該產品憑證設計的 `items` 裡（例：`channel=line,slack`）。 */
  unknown_module_items?: string[]
}

/** 批65：授權套裝預設值（簽發表單「選套裝自動帶入」；enterprise＝全客製）。 */
export interface PlanPreset {
  label: string
  seats?: number
  max_product_lines?: number | null
  modules: Record<string, string>
  quotas: Record<string, number | null>
}
/**
 * 某產品**可販售**的套裝預設值（`kind='plan'` 且啟用）。批93 起路徑帶產品 key。
 *
 * ★ 多數畫面其實不必打這一支——`listProducts()` 回的 `ProductSpec.plans` 已含全部套裝，
 *   用 `lib/products.ts::sellablePlans(spec)` 就地篩即可（少一次往返、也保證與憑證設計
 *   同一份）。這支留給「只知道產品 key、手上沒有 spec」的呼叫端。
 */
export const planPresets = (productKey: string) =>
  api<Record<string, PlanPreset>>(`/api/admin/products/${encodeURIComponent(productKey)}/plans`)

// --- 站台指標的顯示定義（批112）---
//
// ★ 這不是產品宣告的東西：`deployments.metrics` 的鍵由產品端自己決定，這張表只回答
//   「那個英文鍵在我方後台要印成什麼樣子」，由我方營運人工維護、與產品版本脫鉤
//   （後端 `api/admin_metrics.py` 檔頭有完整論證）。讀＝staff、寫＝manager。

export interface MetricDefinition {
  id: string
  product: string
  key: string
  label: string
  /** 純顯示後綴（`筆`／`點`／`%`…）；LS 不做任何單位換算。 */
  unit: string | null
  value_format: 'integer' | 'decimal' | 'percent'
  decimals: number
  sort_order: number
  hint: string | null
  /** 收得到但不顯示。**不是刪**——刪了它下次回報又會回到「未定義」清單。 */
  hidden: boolean
  updated_at?: string | null
}

export interface MetricDefinitionIn {
  product: string
  key: string
  label: string
  unit?: string | null
  value_format?: string
  decimals?: number
  sort_order?: number
  hint?: string | null
  hidden?: boolean
}

export const listMetricDefs = (product?: string) =>
  api<{ items: MetricDefinition[] }>(`/api/admin/metric-defs${qs({ product })}`).then(
    (r) => r.items,
  )

export const createMetricDef = (body: MetricDefinitionIn) =>
  api<MetricDefinition>('/api/admin/metric-defs', { method: 'POST', body })

/** 只改顯示欄——`product`／`key` 是這份定義的身分，建立之後改不動（後端也不收）。 */
export const updateMetricDef = (id: string, body: Omit<MetricDefinitionIn, 'product' | 'key'>) =>
  api<MetricDefinition>(`/api/admin/metric-defs/${id}`, { method: 'PUT', body })

export const deleteMetricDef = (id: string) =>
  api<void>(`/api/admin/metric-defs/${id}`, { method: 'DELETE', raw: true })

// --- 設定樣板（批112／1.0.44）---
//
// 「一台代管主機要提供給站台的環境變數」的欄位宣告。1.0.43 之前那是產品的屬性
// （`products.agent_config_schema_json`）；現在是我方後台維護的**資料**——建立／匯入／
// 編輯樣板，再到主機頁掛上去（掛載端點在 `api/hosting.ts`）。
//
// 讀＝staff、寫＝manager。代理商 realm 與客戶 portal **完全沒有**這一族端點。

/** 樣板的一個欄位宣告。`env_key` 是後端組好的（`<NAMESPACE>_<KEY>`），唯讀。 */
export interface AgentConfigTemplateField {
  key: string
  label: string
  type: 'string' | 'number' | 'bool'
  /** true＝值需加密：後台只顯示遮罩、下發為密文、不可進派工參數。 */
  secret: boolean
  absent_default: string | number | boolean | null
  hint: string | null
  deprecated: boolean
  /** 後端組好的環境變數名（只在清單／單筆回應有；匯出格式刻意不帶）。 */
  env_key?: string
  options?: string[]
  min?: number | null
  max?: number | null
}

export interface AgentConfigTemplate {
  id: string
  name: string
  /** 環境變數前綴（`^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$`）。 */
  namespace: string
  description: string | null
  fields: AgentConfigTemplateField[]
  /** 有幾台主機掛著它（>0 就不能刪）。 */
  attached_hosts: number
  updated_at?: string | null
}

/** 建立／編輯／匯入的形狀（匯出回的也是這個，少了 `id`／`attached_hosts`）。 */
export interface AgentConfigTemplateIn {
  name: string
  namespace: string
  description?: string | null
  fields: AgentConfigTemplateField[]
}

/** 改 namespace／刪 key 之後對不到宣告的已存值（值不動，只是警示）。 */
export interface OrphanedValue {
  host: string
  namespace: string
  key: string
}

export const listAgentConfigTemplates = () =>
  api<{ items: AgentConfigTemplate[] }>('/api/admin/agent-config-templates').then((r) => r.items)

export const exportAgentConfigTemplate = (id: string) =>
  api<AgentConfigTemplateIn>(`/api/admin/agent-config-templates/${id}/export`)

export const createAgentConfigTemplate = (body: AgentConfigTemplateIn) =>
  api<AgentConfigTemplate>('/api/admin/agent-config-templates', { method: 'POST', body })

export const updateAgentConfigTemplate = (id: string, body: AgentConfigTemplateIn) =>
  api<AgentConfigTemplate & { orphaned_values: OrphanedValue[] }>(
    `/api/admin/agent-config-templates/${id}`,
    { method: 'PUT', body },
  )

export const deleteAgentConfigTemplate = (id: string) =>
  api<void>(`/api/admin/agent-config-templates/${id}`, { method: 'DELETE', raw: true })

/** 匯入＝依 `name` upsert（單筆或陣列都收）。 */
export const importAgentConfigTemplates = (body: AgentConfigTemplateIn | AgentConfigTemplateIn[]) =>
  api<{ created: string[]; updated: string[]; orphaned_values: OrphanedValue[] }>(
    '/api/admin/agent-config-templates/import',
    { method: 'POST', body },
  )

// --- 後台員工帳號 / 角色 ---
/**
 * 自己是誰。`reseller_id`／`product_keys` 是批93-C 加的（舊後端不帶＝視同我方員工、不限產品）。
 * `product_keys` **null＝不限**（不是空陣列——空陣列是「一個產品都不能碰」）。
 *
 * 批99-A 加兩欄，語意刻意與 `product_keys` **相反**：
 * - `hosting_visible`：這個帳號看不看得到代管中台（＝有效範圖 ∩ 支援代管的產品 ≠ ∅）。
 *   缺席（舊後端）＝**看不到**（fail-closed，見 `stores/auth.ts`）。
 * - `hosting_product_keys`：具體可以開站的產品鍵，**永遠是陣列、沒有 null＝不限**這回事
 *   （代管佔我方 VM 資源，「不限」在這裡沒有安全的解讀）。
 */
export const getMe = () =>
  api<{
    email: string
    role: string
    reseller_id?: string | null
    reseller_name?: string | null
    product_keys?: string[] | null
    hosting_visible?: boolean
    hosting_product_keys?: string[]
  }>('/api/admin/me')
export const listAdminUsers = () => api<AdminUserRow[]>('/api/admin/admin-users')
/**
 * 修改帳號。`product_keys` 送 null＝改回「不限」、送陣列＝限定那些產品。
 * ⚠️ **staff ↔ reseller 不可互轉**（後端 422「請刪除後重建」）——角色下拉在既有列上
 * 也因此只給同一族的選項。
 */
export const patchAdminUser = (
  id: string,
  body: {
    role?: string
    active?: boolean
    name?: string | null
    /** `null`＝不限（代理商窗口＝沿用公司範圍）；`[]`＝暫停簽發權，兩者語意不同。 */
    product_keys?: string[] | null
    /** 批107：搬公司（**只能在代理商窗口之間**；跨 realm 後端 422）。 */
    reseller_id?: string | null
  },
) => api<AdminUserRow>(`/api/admin/admin-users/${id}`, { method: 'PUT', body })
export const deleteAdminUser = (id: string) =>
  api<null>(`/api/admin/admin-users/${id}`, { method: 'DELETE' })
/**
 * 邀請／預建後台帳號（僅 superadmin）。**不寄信**——請自行通知對方用 Google 登入。
 * **不鎖網域**（批93-C 修正過期註解）：我方 Workspace 網域免預建自動建帳，其他網域
 * （含代理商的自家 Google 帳號）預建後即可登入。
 *
 * 422 的五道驗證：`role='reseller'` 缺 `reseller_id`／非 reseller 卻帶 `reseller_id`／
 * reseller 用我方 Workspace 網域／`product_keys` 有不存在的產品／帳號範圖不是公司範圖的子集。
 * email 已存在 → 409，detail 帶 `{code:'exists',role,active}`。
 */
export const createAdminUser = (body: {
  email: string
  role?: string
  name?: string | null
  reseller_id?: string | null
  product_keys?: string[] | null
}) => api<AdminUserRow>('/api/admin/admin-users', { method: 'POST', body })
/** 強制登出：作廢該帳號此刻之前簽出的所有 token（**可以對自己用**，會連本機 session 一起踢）。 */
export const revokeAdminUserTokens = (id: string) =>
  api<{ email: string; token_not_before: string; self: boolean }>(
    `/api/admin/admin-users/${id}/revoke-tokens`,
    { method: 'POST' },
  )

// --- 稽核紀錄（僅 superadmin）---
export interface AuditQuery {
  actor?: string
  action?: string
  targetType?: string
  /** 續頁游標＝上一頁回的 `next`（兩個欄位要一起帶，只帶一個會被後端忽略）。 */
  cursor?: AuditCursor | null
  /** 1–200，後端預設 50。 */
  limit?: number
}
export const listAuditLogs = (q: AuditQuery = {}) => {
  const sp = new URLSearchParams()
  if (q.actor) sp.set('actor', q.actor)
  if (q.action) sp.set('action', q.action)
  if (q.targetType) sp.set('target_type', q.targetType)
  if (q.limit) sp.set('limit', String(q.limit))
  if (q.cursor) {
    sp.set('before_at', q.cursor.at)
    sp.set('before_id', q.cursor.id)
  }
  const s = sp.toString()
  return api<AuditPage>(`/api/admin/audit-logs${s ? `?${s}` : ''}`)
}
/** 動作字典（篩選下拉的來源；後端回**全表**，不是 DISTINCT——沒發生過的動作也在裡面）。 */
export const auditMeta = () => api<{ actions: Record<string, string> }>('/api/admin/audit-logs/meta')

/**
 * `kind` 可選 `company`／`personal`／`unset`（還沒填基本資料的舊客戶）。
 *
 * 批93-C 兩個新過濾（都是**伺服器端**）：
 * - `resellerId`＝該代理商的客戶；特別值 **`'none'`＝只看直營**（`reseller_id IS NULL`）
 * - `empty`＝空組織（無授權、無申請、基本資料未填）——不自動歸戶之後會長出來的殘渣，
 *   清理用；不做自動排程，逐筆確認後刪。
 *
 * ★ 批93-D 起是 **keyset 分頁**：回 `{items, next_cursor}`（不再是裸陣列），而且排序
 *   從舊到新改成**新到舊**。續頁把上一頁的 `next_cursor` 原樣帶回來。
 */
export const listCustomers = (
  opts: {
    kind?: string
    resellerId?: string
    empty?: boolean
    /** 批141：`all`（預設）｜`direct`｜`platform`（多租戶平台的組織，含已解除關聯的）。 */
    source?: CustomerSource
    limit?: number
    cursor?: string | null
  } = {},
) =>
  api<CustomerPage>(
    `/api/admin/customers${qs({
      kind: opts.kind || undefined,
      reseller_id: opts.resellerId || undefined,
      source: opts.source && opts.source !== 'all' ? opts.source : undefined,
      empty: opts.empty ? '1' : undefined,
      limit: opts.limit ? String(opts.limit) : undefined,
      cursor: opts.cursor || undefined,
    })}`,
  )
/**
 * 指派／改派客戶的歸屬代理商（admin+）。`resellerId` 送 **null＝轉直營**。
 *
 * ⚠️ 這是**替換**語意，不是加掛：一個客戶只能屬於一個代理商。改派後既有授權與申請上的
 * `reseller_id` 快照**不動**（那是當時的經手紀錄），只影響之後的可見性與新申請歸屬。
 */
export const setCustomerReseller = (customerId: string, resellerId: string | null) =>
  api<Customer>(`/api/admin/customers/${customerId}/reseller`, {
    method: 'POST',
    body: { reseller_id: resellerId },
  })
/** 單一客戶的完整檔案（基本資料＋邀請關係）——詳情頁進來時打一次。 */
export const getCustomer = (id: string) => api<CustomerDetail>(`/api/admin/customers/${id}`)
export const createCustomer = (name: string, domain?: string) =>
  api<Customer>('/api/admin/customers', { method: 'POST', body: { name, domain: domain || null } })
/**
 * `PUT /customers/{id}` 的 body（批97 Bug 7 起含**基本資料全欄**）。
 *
 * ★ **部分更新**：只有出現在物件上的鍵會被寫入（後端讀 pydantic 的 `model_fields_set`），
 *   所以「只改電話」不會清掉職稱／產業／通路。**別為了湊齊形狀補一堆 `undefined`**——
 *   `JSON.stringify` 會把 `undefined` 的鍵丟掉，但明確帶 `null` 就是「清空這一欄」。
 * ★ 電話收**兩段**（`country_code` ＋ `phone_number`），與 portal 表單同形；DB 存的是
 *   E.164，由後端 `normalize_phone` 組。前端不要自己組（組錯的方式有很多種）。
 * ★ 條款同意與邀請碼**不可代填**（後端基底上沒有那些欄，多帶會被 pydantic 靜默丟掉）。
 *
 * 與代理商 realm 的 body 是**同一個型別**（`api/reseller.ts` import 這一支）：後端兩支
 * 端點共用 `services/customer_patch.py`，前端共用 `components/CustomerProfileCard.vue`。
 */
export interface CustomerPatchBody {
  name?: string
  domain?: string | null
  notes?: string | null
  // --- `customers` 上的開欄 ---
  kind?: string | null
  contact_name?: string | null
  country_code?: string | null
  phone_number?: string | null
  tax_id?: string | null
  contact_email?: string | null
  address?: string | null
  marketing_opt_in?: boolean
  // --- `profile_json` 的十鍵（批114 裁示 ㉞ 拿掉了「主要客服通路」那一鍵）---
  title?: string | null
  line_id?: string | null
  industry?: string | null
  heard_from?: string | null
  heard_from_other?: string | null
  company_size?: string | null
  /** 批114（裁示 ㉘）：預計規模（原 `expected_seats`；後端輸入端同時吃舊鍵）。 */
  expected_scale?: number | null
  website?: string | null
  city?: string | null
  needs?: string | null
}
/**
 * 部分更新（只送有帶的欄）；網域撞到別的客戶 → 409、基本資料欄位不合法 → 422
 * `{code, field, message}`（`bad_option`／`bad_phone`／`bad_tax_id`／`required`）。
 *
 * 回應是 **additive**：原本的客戶投影 ＋ 一個 `profile` 鍵，存檔後直接拿它換掉畫面、
 * 不必再打一次 `GET /customers/{id}`。
 */
export const updateCustomer = (id: string, body: CustomerPatchBody) =>
  api<Customer & { profile: CustomerProfile }>(`/api/admin/customers/${id}`, {
    method: 'PUT',
    body,
  })

/** 選項清單的一項：`value` 進 DB（穩定、可統計），`label` 給人看。 */
export interface ProfileOption {
  value: string
  label: string
}
/**
 * 客戶基本資料表單的下拉選項（批97 Bug 7）——**唯一真相在後端**
 * （`services/profile_options.py`，與 portal 的 `GET /api/portal/profile/options` 逐字相同）。
 *
 * 後台**不硬編**這幾組清單：value 同時是顯示文字與 DB 內容，兩邊各寫一份的下場是
 * 「後台多了一個選項、後端 `bad_option` 擋下來，而錯誤訊息只說『選項不正確』」。
 */
export interface CustomerProfileOptions {
  kinds: ProfileOption[]
  country_codes: ProfileOption[]
  industries: ProfileOption[]
  heard_from: ProfileOption[]
  company_sizes: ProfileOption[]
  cities: ProfileOption[]
  terms_version: string
  default_country_code: string
}
/**
 * 取選項清單（admin+）。
 *
 * ⚠️ 路徑刻意**不在** `/customers/*` 底下：`GET /customers/{customer_id}`（uuid）已註冊在前，
 * `/customers/profile-options` 會被它吃掉、把靜態段落當 uuid 解 → 422。代理商 realm 有自己
 * 一支（`api/reseller.ts::getCustomerProfileOptions`），理由是結構性的。
 */
export const getCustomerProfileOptions = () =>
  api<CustomerProfileOptions>('/api/admin/customer-profile-options')
/** 刪除客戶前的確認資料（僅 superadmin）：會一併消失的筆數；有生效中授權＝不可刪。 */
export interface CustomerDeletionPreview {
  id: string
  name: string
  licenses: number
  active_licenses: number
  requests: number
  users: number
  links: number
  deployments: number
  /** 批93-B：加購子憑證（跟著 `customers` FK CASCADE 一起消失——那是另外收過錢的東西）。 */
  addons: number
  /** 批141：平台客戶一律 false（平台客戶不可刪除）。 */
  deletable: boolean
  /** 批141：這個客戶是合作平台的組織（null＝不是）。 */
  platform?: PlatformRef | null
  /** 批153：私人 plugin 的權利（含已撤銷的歷史列），跟著客戶一起刪掉。 */
  plugin_grants?: number
  /** 批153：這個客戶是 plugin 發行者（null＝不是）——發行者一律 `deletable=false`。 */
  plugin_publisher?: CustomerPluginPublisherRef | null
}
/** 批153：刪除預覽裡的發行者摘要（`plugin_publishers` 每客戶至多一列）。 */
export interface CustomerPluginPublisherRef {
  id: string
  display_name: string
  key_prefix: string | null
  status: string
}
export const customerDeletionPreview = (id: string) =>
  api<CustomerDeletionPreview>(`/api/admin/customers/${id}/deletion-preview`)
/** 永久刪除客戶（僅 superadmin）；尚有生效中授權 → 409。 */
export const deleteCustomer = (id: string) =>
  api<null>(`/api/admin/customers/${id}`, { method: 'DELETE' })

// --- 客戶合併 A→B（批93-C §2.7；僅 superadmin）------------------------------
//
// 為什麼不是「改個 customer_id 就好」：已簽出的授權 payload 內嵌 `customer_id`／
// `customer_name`，子憑證的綁定鍵也含 `customer_id` → 只改 DB 欄會讓 B 名下新加購的
// 子證與 A 時期簽的主證對不起來，產品端直接拒收。所以合併＝**把 A 的 active 主證與
// 子證同內容重簽到 B**（接 supersedes 鏈），線上站靠心跳自動換證。

export interface MergePreview {
  /** 會搬過去的成員數。 */
  members: number
  /** A 名下的授權總數（其中 active 的會被重簽）。 */
  licenses: number
  /** 需要重簽的張數（active 主證＋子證）。 */
  resign_count: number
  /** 重簽了也吃不到的站台（airgapped 不心跳）——要人工把新證送過去。 */
  airgapped: number
  requests: number
  hosted_sites: number
  /** A 與 B 的歸屬代理商不同 → 預設擋下（要嘛先改派、要嘛 `force_reseller` 以 B 為準）。 */
  reseller_mismatch: boolean
  /** 兩邊都有 domain 且不同 → B 保留、A 的寫進 notes。 */
  domain_conflict: boolean
  /** 兩邊都有的 email（合併時保留 B 的列、刪 A 的重複）。 */
  duplicate_members: string[]
  // --- 以下是後端額外附帶的顯示資訊（**確認框的數字一律用上面那幾個扁平欄位**）---
  /** 人話版的注意事項（網域衝突／重複帳號／代理商不同）。 */
  warnings?: string[]
  /** 只算 active 主證（`resign_count` 還含子證），拆開給需要分別顯示時用。 */
  resign_licenses?: number
  /** 會被重簽但吃不到新證的 license_id 明細（`airgapped` 是它的長度）。 */
  airgapped_licenses?: { license_id: string; [k: string]: unknown }[]
  source?: Customer
  target?: Customer
  counts?: Record<string, number>
  /** 批141：任一方是合作平台的組織＝不能合併（`merge` 會 409 `platform_managed_customer`）。 */
  platform_blocked?: boolean
  /** 批153：來源客戶是 plugin 發行者＝不能合併（`merge` 會 409 `plugin_publisher_customer`）。 */
  plugin_publisher_blocked?: boolean
}
export const mergePreview = (sourceId: string, targetId: string) =>
  api<MergePreview>(
    `/api/admin/customers/${sourceId}/merge-preview${qs({ target_id: targetId })}`,
  )

export interface MergeResult {
  moved: Record<string, number>
  /** 重簽出來的**新** license_id（結果頁要列的就是「哪幾張新證要送出去」）。 */
  resigned_licenses: string[]
  resigned_addons: string[]
  airgapped: string[]
  warnings: string[]
  target_id?: string
  /** 舊→新的完整對應；事後追查的正本在稽核 detail，這裡是順手附上的顯示資料。 */
  resigned_license_pairs?: { from: string; to: string }[]
  resigned_addon_pairs?: { from: string; to: string }[]
}
/**
 * 把 `sourceId`（A）併入 `targetId`（B）。**A 會被硬刪**（快照存進稽核 detail）。
 * `confirmName` 必須逐字等於 A 的名稱（防手滑選錯方向）。
 */
export const mergeCustomers = (
  targetId: string,
  body: { source_id: string; confirm_name: string; force_reseller?: boolean },
) => api<MergeResult>(`/api/admin/customers/${targetId}/merge`, { method: 'POST', body })

// --- 代理商公司（批93-C §2.2；superadmin）-----------------------------------

export interface Reseller {
  id: string
  name: string
  /** 短代號（選填，人工對帳用）；不填＝null，多個 null 不算撞名。 */
  code: string | null
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  /**
   * 這家代理商能賣的產品（`products.key`）。**null＝不限**，但建立時後端強制非空——
   * 否則之後上線的新產品會靜默對所有代理商開放。
   */
  product_keys: string[] | null
  /** 停權後該公司底下所有帳號一律 403（`require_reseller` 會檢公司 active）。 */
  active: boolean
  notes: string | null
  created_by: string | null
  created_at: string
  /** 名下客戶數（清單用；後端一次 GROUP BY 批載）。 */
  customer_count?: number
  // --- 批107：公司基本資料與合約（**我方營運資料**，不下發客戶 portal）---
  // ★ 一律選填：既有測資與其他頁面的 `Reseller` 物件（例如 spec 的 fixture）不必補齊。
  tax_id?: string | null
  address?: string | null
  /** 業務轄區（自由文字）。欄名不叫 region——`managed_hosts.region` 是 GCP region。 */
  territory?: string | null
  company_phone?: string | null
  website?: string | null
  /** `YYYY-MM-DD`。純資訊：到期不停權，只在清單顯示徽章。 */
  contract_starts_on?: string | null
  contract_ends_on?: string | null
  account_manager_id?: string | null
  account_manager?: { id: string; email: string; name: string | null; active: boolean } | null
}
export interface ResellerInput {
  name: string
  code?: string | null
  contact_name?: string | null
  contact_email?: string | null
  contact_phone?: string | null
  /** 建立時必填且非空；更新時省略＝不動。 */
  product_keys?: string[] | null
  active?: boolean
  notes?: string | null
  // --- 批107 ---
  tax_id?: string | null
  address?: string | null
  territory?: string | null
  company_phone?: string | null
  website?: string | null
  contract_starts_on?: string | null
  contract_ends_on?: string | null
  account_manager_id?: string | null
}
export const listResellers = () => api<Reseller[]>('/api/admin/resellers')
export const createReseller = (body: ResellerInput) =>
  api<Reseller>('/api/admin/resellers', { method: 'POST', body })
export const updateReseller = (id: string, body: ResellerInput) =>
  api<Reseller>(`/api/admin/resellers/${id}`, { method: 'PUT', body })

/**
 * 授權清單。過濾參數都是**伺服器端**（原則：下拉送伺服器、桶留前端）。
 * `hostId`＝代管主機（後端換成該主機在役站台的 instance_id 集合）。
 *
 * ★ 批93-D 起是 **keyset 分頁**：回 `{items, next_cursor}`（不再是裸陣列），
 *   而且**不含 `blob`**。續頁把上一頁的 `next_cursor` 原樣帶回來。
 */
export const listLicenses = (
  opts: {
    customerId?: string
    hostId?: string
    product?: string
    /** 批107：代理商過濾。uuid＝該代理商；`'none'`＝直營（證上沒有代理商）。 */
    resellerId?: string
    /**
     * 批121：狀態桶（`active`／`pending_activation`／`expiring_soon`／`expired`）。
     * **伺服器端過濾**——以前這四個籤是前端算的，於是計數只算得到「已載入的那幾頁」，
     * 而「載入更多」載回的是下一頁原始資料、在某個籤上常常一列都沒增加。
     */
    bucket?: string
    limit?: number
    cursor?: string | null
  } = {},
) =>
  api<LicensePage>(
    `/api/admin/licenses${qs({
      customer_id: opts.customerId,
      host_id: opts.hostId,
      reseller_id: opts.resellerId,
      product: opts.product,
      bucket: opts.bucket || undefined,
      limit: opts.limit ? String(opts.limit) : undefined,
      cursor: opts.cursor || undefined,
    })}`,
  )

/** 四個狀態桶＋`all` 的**全表**筆數（批121）。過濾條件要與 `listLicenses` 一致。 */
export interface LicenseCounts {
  all: number
  active: number
  pending_activation: number
  expiring_soon: number
  expired: number
}
export const licenseCounts = (
  opts: { customerId?: string; hostId?: string; product?: string; resellerId?: string } = {},
) =>
  api<LicenseCounts>(
    `/api/admin/licenses/counts${qs({
      customer_id: opts.customerId,
      host_id: opts.hostId,
      reseller_id: opts.resellerId,
      product: opts.product,
    })}`,
  )
/**
 * 直接簽發（不經申請流程）。批93-C 起**權限由套裝的核發政策決定**：
 * - 政策放行（`admin`／`reseller` 級套裝，且內容等於模板）→ 照簽，`issued_via='policy'`
 * - 政策不放行但你是 superadmin → 照簽，但標 `issued_via='direct'`＋稽核＋發告警
 * - 政策不放行且你不是 superadmin → **403 `activation_requires_approval`**
 *   → 前端要改導向「建立授權草稿」（`createLicenseRequest`）
 */
export const issueLicense = (customerId: string, input: IssueInput) =>
  api<License & UnknownModulesWarning & { direct?: boolean }>(
    `/api/admin/customers/${customerId}/licenses`,
    { method: 'POST', body: input },
  )
export const revokeLicense = (licenseId: string) =>
  api<License>(`/api/admin/licenses/${licenseId}/revoke`, { method: 'POST' })
export const deleteLicense = (licenseId: string) =>
  api<null>(`/api/admin/licenses/${licenseId}`, { method: 'DELETE' })
export const renewLicense = (licenseId: string, expiresAt: string) =>
  api<License>(`/api/admin/licenses/${licenseId}/renew`, { method: 'POST', body: { expires_at: expiresAt } })

/**
 * 取回授權原文（批108）。**「下載授權檔」與「複製憑證」共用這一支**——兩種帶走方式
 * 都會記到同一筆稽核（`license.download`）。
 *
 * 只給生效中的證（撤銷／被取代的回 409 `license_not_active`）；代管站一律 403
 * `hosted_instance`（那些站的授權由中台派送）。
 */
export const licenseFile = (licenseId: string) =>
  api<{
    license_id: string
    customer_id: string
    customer_name: string | null
    product: string
    status: string
    expires_at: string | null
    blob: string
  }>(`/api/admin/licenses/${encodeURIComponent(licenseId)}/file`)

/** 換發新證的內容覆寫（沒給的欄一律從基準證沿用）。 */
export interface ReissueInput {
  plan?: string | null
  modules?: Record<string, string> | null
  /**
   * 批114：數量一律走這一包（原本還有兩個一級數字欄，後端仍相容收下，後台不再送）。
   * 與 `modules`／`policy` 同一條規則：**有送才改**，沒送＝沿用基準證。
   */
  quotas?: Record<string, unknown> | null
  /** 期限＝`term_days * 1440 + term_minutes`；兩個都不給＝沿用基準證。 */
  term_days?: number | null
  term_minutes?: number | null
  /** `on_fetch`＝首次取證才起算（預設）／`keep`＝沿用原到期日／`at`＝指定時刻。 */
  expiry_mode?: 'on_fetch' | 'keep' | 'at'
  expires_at?: string | null
  /**
   * 批110（#5495）：行為參數也可以逐項改（`modules`／`quotas` 早就在上面了，
   * 只是批108 的前端沒開）。**有送才改**——送了就是偏離套裝模板，政策閘會把它升到
   * 「營運主管」。★ 代理商 realm 送 `policy`／`not_before` 一律 422 `field_not_allowed`。
   */
  policy?: Record<string, unknown> | null
  /** 批110（#5496）：延後生效；省略＝沿用基準證的值。 */
  not_before?: string | null
  note?: string | null
}

/**
 * 換發新證（批108）：從既有的證換一張，可同時改套裝／模組／上限／期限。
 *
 * ★ 基準證**任何狀態都可以**（active／過期／superseded／revoked）——判準是「機器是正確的」，
 *   基準證只是提供機器身分與內容範本。救援時最常見的正是已撤銷／已被取代的那兩種。
 * ★ 權限走與直接簽發同一把尺：內容偏離套裝模板會 403 `activation_requires_approval`，
 *   前端據此導向授權申請流程。
 */
export const reissueLicense = (licenseId: string, input: ReissueInput) =>
  api<License & { superseded: string | null; base_status: string; policy: string }>(
    `/api/admin/licenses/${encodeURIComponent(licenseId)}/reissue`,
    { method: 'POST', body: input },
  )

// --- 批次補模組鍵（批81 需求 10）---
// CS 的 entitlement 語意是「鍵缺席＝停用」，新增模組鍵時所有既有授權都少那個鍵。
// 部署順序：LS 先補鍵 → 各站取得新證 → 才升 CS。
export interface BackfillRow {
  license_id: string
  customer_id: string
  customer_name: string | null
  host_instance_id: string | null
  instance_name: string | null
  plan: string | null
  expires_at: string | null
  term_days: number | null
  trial: boolean
  hosted: boolean
  deployment_mode: string
  missing: string[]
}
export interface BackfillResigned {
  customer_name: string | null
  instance_name: string | null
  old_license_id: string
  new_license_id: string
  added: string[]
}
export interface BackfillResult {
  dry_run: boolean
  modules: Record<string, string>
  to_resign?: number
  rows?: BackfillRow[]
  resigned?: BackfillResigned[]
  skipped: number
}
export interface BackfillInput {
  /** 批93：**必填**。`all=true` 的語意是「**該產品的**全部授權」，不帶會掃到別的產品。 */
  product: string
  modules: Record<string, string>
  customer_id?: string
  host_instance_id?: string
  all?: boolean
  dry_run: boolean
}
export const backfillModules = (body: BackfillInput) =>
  api<BackfillResult>('/api/admin/licenses/backfill-modules', { method: 'POST', body })

/**
 * 部署清單。`customerId`／`hostId` 是**伺服器端**過濾；型態桶與在線/離線留前端。
 * ⚠️ 本端點 operator 可讀，但客戶下拉的來源 `GET /customers` 是 admin 限定
 * ——呼叫端要自己顧好「operator 不載客戶清單」。
 *
 * ★ 批93-D 起是 **keyset 分頁**：回 `{items, next_cursor}`。排序鍵也從「最後回報」改成
 *   **建立時間**（新到舊）——游標的排序鍵必須不可變，`last_seen` 每次心跳都會變，
 *   翻頁到一半剛好回報的那台會被默默跳過。離線判定看的是欄位值不是列的順序，不受影響。
 */
export const listDeployments = (
  opts: {
    customerId?: string
    hostId?: string
    /** 批107：代理商過濾（判準是這台回報的那張證的簽發快照）。`'none'`＝直營。 */
    resellerId?: string
    /** 批171：產品過濾（產品 key；判準是這台回報的那張證的產品）。 */
    product?: string
    limit?: number
    cursor?: string | null
  } = {},
) =>
  api<DeploymentPage>(
    `/api/admin/deployments${qs({
      customer_id: opts.customerId,
      host_id: opts.hostId,
      reseller_id: opts.resellerId,
      product: opts.product,
      limit: opts.limit ? String(opts.limit) : undefined,
      cursor: opts.cursor || undefined,
    })}`,
  )

// --- 主機（一客戶多主機，每主機一張獨立授權）---
export const listCustomerHosts = (customerId: string) =>
  api<CustomerHosts>(`/api/admin/customers/${customerId}/hosts`)
export const setHostLabel = (customerId: string, instanceId: string, label: string | null) =>
  api<{ instance_id: string; label: string | null }>(
    `/api/admin/customers/${customerId}/hosts/${encodeURIComponent(instanceId)}`,
    { method: 'PUT', body: { label } },
  )
export const transferHost = (
  customerId: string,
  instanceId: string,
  newInstanceId: string,
  newFingerprint?: Record<string, string> | null,
) =>
  api<License>(
    `/api/admin/customers/${customerId}/hosts/${encodeURIComponent(instanceId)}/transfer`,
    { method: 'POST', body: { new_instance_id: newInstanceId, new_fingerprint: newFingerprint ?? null } },
  )
export const disableHost = (customerId: string, instanceId: string) =>
  api<{ instance_id: string; revoked_licenses: number }>(
    `/api/admin/customers/${customerId}/hosts/${encodeURIComponent(instanceId)}/disable`,
    { method: 'POST' },
  )

/**
 * 換機申請清單。★ 批93-D 起是 **keyset 分頁**：回 `{items, next_cursor}`。
 * 這是張只增不刪的紀錄表（核准後留痕），所以它是最典型的成長型清單。
 */
export const listRebindRequests = (opts: { limit?: number; cursor?: string | null } = {}) =>
  api<RebindRequestPage>(
    `/api/admin/rebind-requests${qs({
      limit: opts.limit ? String(opts.limit) : undefined,
      cursor: opts.cursor || undefined,
    })}`,
  )
export const approveRebind = (id: string) =>
  api<{ status: string; issued_license_id: string }>(`/api/admin/rebind-requests/${id}/approve`, {
    method: 'POST',
  })

/**
 * 開通（approve）的 body。
 *
 * ★★ 批93-C **移除了全部內容覆寫欄位**（原本 approve 可以就地改席次／模組／配額）。
 *   開通＝「照原樣簽發已確認鎖定的內容」；要改內容就 `returnLicenseRequest` 退回草稿，
 *   由送件那一方改完再確認一次。這是審核能成立的前提——審核者能改內容的話，
 *   maker 確認過的東西就不算數了。
 */
/**
 * 開通授權申請。
 *
 * 批121：**可以順手改內容**（營運主管以上）——帶哪個鍵就覆寫哪一格，不帶＝照申請單原樣。
 * 只送「真的改過」的鍵（`lib/requestOverride.ts::changedContentKeys`）：整包送會讓「什麼
 * 都沒動」也被記成一次覆寫，而且政策會拿表單展開後的值重算，把照模板的申請判成客製內容。
 * ★ `note`（申請單的公開備註）刻意不在這裡：那是送件方的欄位，審核者要寫的是 `review_note`。
 */
export type ApproveInput = Omit<RequestDraftInput, 'note'> & {
  /** 開通備註（**內部**，客戶看不到）。 */
  review_note?: string | null
}

/**
 * 開通流程的清單過濾（都是伺服器端）。`status` 含新的 `draft`。
 *
 * ★ 批93-D 起是 **keyset 分頁**：回 `{items, next_cursor}`（不再是裸陣列）。
 * ★ 批121 §9：`product` 與 `only_returned` 從前端桶下推伺服器——頁籤上的數字改由
 *   `licenseRequestCounts()` 出，「載入更多」載的才是**同一個籤**的下一頁。
 */
export const listLicenseRequests = (
  opts: {
    origin?: string
    status?: string
    resellerId?: string
    /** 批141：申請人是這個多租戶平台的組織（與 `origin` 正交）。 */
    platformId?: string
    product?: string
    /** 批155：核發方式（`platform`＝平台自動核准）。 */
    issuedVia?: string
    onlyReturned?: boolean
    limit?: number
    cursor?: string | null
  } = {},
) =>
  api<LicenseReqPage>(
    `/api/admin/license-requests${qs({
      origin: opts.origin || undefined,
      status: opts.status || undefined,
      reseller_id: opts.resellerId || undefined,
      platform_id: opts.platformId || undefined,
      product: opts.product || undefined,
      issued_via: opts.issuedVia || undefined,
      only_returned: opts.onlyReturned ? 'true' : undefined,
      limit: opts.limit ? String(opts.limit) : undefined,
      cursor: opts.cursor || undefined,
    })}`,
  )

/**
 * 申請狀態桶的**全表**筆數（批121 §9）。
 *
 * 吃的過濾條件與 `listLicenseRequests` 相同，只少 `status`／`only_returned`／`cursor`
 * ——這支回的就是「每個桶各有幾筆」。`returned` 與 `draft` 刻意重疊（草稿籤的子過濾）。
 */
export interface LicenseRequestCounts {
  all: number
  draft: number
  pending: number
  approved: number
  rejected: number
  cancelled: number
  returned: number
}
export const licenseRequestCounts = (
  opts: { origin?: string; resellerId?: string; platformId?: string; product?: string; issuedVia?: string } = {},
) =>
  api<LicenseRequestCounts>(
    `/api/admin/license-requests/counts${qs({
      origin: opts.origin || undefined,
      reseller_id: opts.resellerId || undefined,
      platform_id: opts.platformId || undefined,
      product: opts.product || undefined,
      issued_via: opts.issuedVia || undefined,
    })}`,
  )

/**
 * 批93：我方後台**代客戶**建立一筆授權申請（貼上／上傳客戶 email 來的 `request.json`）。
 *
 * 補的是「離線客戶把檔案寄給我們、後台卻沒有入口」的缺口——在這之前只能請客戶自己去
 * portal 上傳，或直接硬簽一張沒有申請紀錄／沒有指紋的證。
 *
 * 錯誤碼（`detail.code`）：
 * - 422 `product_mismatch`＝下拉選的產品與檔案內的 `product` 不一致（不猜，擋下）
 * - 422 `unknown_product`／`product_deprecated`＝檔案的產品沒建過／已停售
 * - 422 `unknown_plan`＝選的套裝不屬於該產品
 * - 409 `hosted_instance`＝這份檔案指向我方代管的站（核發會讓它當場唯讀）
 * - 409 `pending_exists`＝這台主機已有一筆待審核的申請
 * - 422 `policy_invalid`＝行為參數超出憑證設計宣告的值域（批113）
 * - 422 `expiry_in_the_past`／`not_before_after_expiry`＝啟用／到期時刻不合理（批113）
 */
export const createLicenseRequest = (body: {
  customer_id: string
  request_json: Record<string, unknown>
  product?: string | null
  note?: string | null
  plan?: string | null
  /**
   * ★ 批114：這裡**沒有** `seats_requested`／`quotas_requested`——後端的
   * `AdminLicenseRequestIn` 從來沒有這兩個鍵（它收的是 `seats`／`quotas`），送過去一直被
   * 靜默丟掉。申請內容實際上是靠 `request_json.requested` 帶的，所以直接把失效的鍵刪掉，
   * 行為零改變，也不再讓人以為 LS 有專屬的數量欄。
   */
  /**
   * 批113（#5520／#5521）：草稿也能帶行為參數與啟用／到期時刻。
   * ★ 後端**只信 typed body**——`request_json.requested` 裡同名的三鍵會被剃掉，
   *   所以這裡與 `request_json` 兩邊要帶同一組值（`CustomersView.draftRequestJson()`）。
   */
  policy?: Record<string, unknown> | null
  not_before?: string | null
  expires_at?: string | null
}) =>
  api<{ id: string; status: string; product: string }>('/api/admin/license-requests', {
    method: 'POST',
    body,
  })

/**
 * 修改草稿內容（admin+）。**只限我方草稿**（`origin='admin'`）——代理商草稿回 403
 * `not_owner`：憑證內容是送件那一方的責任，我方只能「退回附理由」請他自己改。
 * 非 draft → 409。
 */
export interface RequestDraftInput {
  plan?: string | null
  modules?: Record<string, string> | null
  /**
   * 數量配額（值 `null`＝無上限）。
   * ★ 批114：草稿表單**只送這一包**——後端仍收得下舊的一級數字欄（`services/
   *   quota_compat.py` 的相容入口），但後台自己不再送，同一個數字只有一個輸入框。
   */
  quotas?: Record<string, number | null> | null
  term_days?: number | null
  /** 批108：期限的分鐘餘數（0–1439）。總期限＝`term_days * 1440 + term_minutes`。 */
  term_minutes?: number | null
  note?: string | null
  /**
   * 批113（#5520／#5521）：行為參數與啟用／到期時刻也進得了草稿。
   * ★ 只有我方草稿的端點收這三鍵——代理商那支（`/reseller/license-requests`）吃的是
   *   基底 schema，型別裡根本沒有它們（fail-closed）。
   */
  policy?: Record<string, unknown> | null
  not_before?: string | null
  expires_at?: string | null
}
export const updateLicenseRequest = (id: string, body: RequestDraftInput) =>
  api<LicenseReq>(`/api/admin/license-requests/${id}`, { method: 'PUT', body })

/**
 * 確認送出（draft → pending）。
 *
 * ★ **政策放行時會當場簽發**（回 `status:'approved'` ＋ `issued_license_id`）——按鈕文字
 *   要依 `policy` 事先分成「確認並核發」與「確認送出（待審核）」，不然操作者按下去才
 *   發現證已經出去了。`policy_reason='custom_content'`＝內容偏離套裝模板，政策被強制
 *   升為 superadmin（前端在編輯時就該提示過）。
 */
export const confirmLicenseRequest = (id: string) =>
  api<{
    status: string
    issued_license_id?: string | null
    policy: string
    policy_reason?: string | null
    /**
     * 批113：草稿裡的「啟用時間」在核發當下已經過去（主人裁示 2：不擋、照簽、帶提示）。
     * true＝期限其實是從**現在**起算，不是從那個時間點。
     */
    not_before_elapsed?: boolean
  }>(`/api/admin/license-requests/${id}/confirm`, { method: 'POST' })

/**
 * 審核通過並核發（pending → approved＝簽發）。`require_admin` ＋ handler 內政策二次收窄。
 *
 * 403 `activation_requires_approval`＝這份內容的政策要 superadmin；
 * 403 `superadmin_required` 同義；批121 起另有 403 `role_required`＝**帶了內容欄位**但
 * 身分不到營運主管（照原內容核發不受這道閘影響）。
 * ★ 批108 起**沒有 `self_approval` 409**——同一個人送件與審核是允許的（這條流程是
 *   「代理商／客戶送件、我方審核」，不是同一組織內的雙人核准），留痕改由稽核
 *   `license_request.approve` 的 `detail.self_approved` 承擔。
 */
export const approveLicenseRequest = (id: string, input: ApproveInput = {}) =>
  api<{
    status: string
    issued_license_id: string
    /** 批81：非 null＝這次核可順手把「試用到期停站」的那個站復站了。 */
    resumed_site_id: string | null
    /**
     * 批113：草稿裡的「啟用時間」在核發當下已經過去（主人裁示 2：不擋、照簽、帶提示）。
     * true＝期限其實是從**現在**起算，不是從那個時間點。
     */
    not_before_elapsed?: boolean
    /** 批121：這次核發實際覆寫掉的欄位名（空陣列＝照申請內容簽的）。 */
    overridden?: string[]
  } & UnknownModulesWarning>(`/api/admin/license-requests/${id}/approve`, {
    method: 'POST',
    body: input,
  })

/**
 * 退回草稿（pending → draft，附理由）。理由**客戶／代理商看得到**。
 * 後端會清空 `activation_policy`／`confirmed_at`／`confirmed_by`，再確認時重算政策。
 */
export const returnLicenseRequest = (id: string, body: { reason: string }) =>
  api<{ status: string }>(`/api/admin/license-requests/${id}/return`, { method: 'POST', body })

/**
 * 對**草稿**附一則註記通知 maker（不改狀態）。`expected_status` 是樂觀鎖——
 * 你在看的時候對方剛好按了「確認送出」的話回 409，避免註記落在已鎖定的內容上。
 */
export const commentLicenseRequest = (id: string, text: string) =>
  api<{ status: string }>(`/api/admin/license-requests/${id}/comment`, {
    method: 'POST',
    body: { text, expected_status: 'draft' },
  })

/**
 * 婉拒授權申請。`reason` 選填但**客戶在 portal 看得到**（別填內部備註）。
 * 409 `{code:'already_reviewed'}`＝這筆已經審核完成（批81 順修：原本沒擋）。
 */
export const rejectLicenseRequest = (id: string, body: { reason?: string | null } = {}) =>
  api<{ status: string }>(`/api/admin/license-requests/${id}/reject`, { method: 'POST', body })

/**
 * 撤回（作廢）授權申請；`draft`／`pending` 皆可，**不分來源**（批97）。
 *
 * `reason` 對他人送出的單子（origin ≠ admin）是**必填**、我方自建草稿選填；
 * 送出去的理由**送件方看得到**（寫進 `request_json.review`）。
 * 沒帶理由時連 body 都不送（後端 body 可省略）。
 * 422 `{code:'reason_required'}`＝撤別人的單子沒附理由。
 */
export const cancelLicenseRequest = (id: string, body?: { reason?: string | null }) =>
  api<{ status: string }>(`/api/admin/license-requests/${id}/cancel`, {
    method: 'POST',
    ...(body ? { body } : {}),
  })

// --- 批81 需求 5：核可代管開站申請 ---

/**
 * 代管試用的授權初值（核可開站對話框的預設帶入）。
 *
 * ⚠️ 刻意**不是** `planPresets()` 的第五筆——試用不是可申請的套裝，只是我方核可代管開站
 * 時的一組預設值（後端 `services/provision.py::trial_preset` 的 docstring 有完整理由）。
 */
export interface TrialPreset extends PlanPreset {
  /** 代管試用天數（產品選項 `hosted_trial_term_days`，預設 30）。套裝沒有這個欄位。 */
  term_days: number
  /** 批108：代管試用一律整天，這一欄恆為 0（讓 `TermField` 不必特例處理缺鍵）。 */
  term_minutes: number
}
export const trialPreset = (productKey: string) =>
  api<TrialPreset>(`/api/admin/products/${encodeURIComponent(productKey)}/trial-preset`)

/**
 * 核可一筆自助申請代管（`kind === 'hosted_provision'`）→ **當場開站**。
 *
 * slug 與客戶填的 setup 資訊由後端從申請單取用，這裡只送「開在哪台」與「這張證的內容」。
 * 409 `not_hosted_request`＝不是代管申請（請用一般核發）；409 `already_reviewed`；
 * 409 `bootstrap_missing`＝申請單缺初始管理員資料（請婉拒並請客戶重送）。
 *
 * ⚠️ 回應**不含** `setup_token`（它隨 job 直接送到那台 VM，中台不再吐第二份）。
 */
export interface ApproveHostedInput {
  host_id: string
  /** 標成試用證＝到期自動停站（核可正式授權時會自動復站）。預設 true。 */
  trial?: boolean
  term_days: number
  /** 批108：期限的分鐘餘數（0–1439）。代管站也允許分鐘級的證（裁示 28）。 */
  term_minutes?: number
  plan?: string | null
  modules?: Record<string, string>
  /**
   * 數量配額。★ 批114：值域從 `number` 放寬成 `number | null`（`null`＝無上限）——
   * 在這之前核可框只能**把那個鍵整個濾掉**才送得出去，等於把客戶明示的「不限」
   * 偷偷變成「沿用模板」。獨立的數量欄已拔除，一律只送這一包。
   */
  quotas?: Record<string, number | null> | null
  /**
   * 批151 C6：開站時一併設定的站台機密（同 `hosting.ts::ProvisionIn.site_secrets`）。
   * **只有我方營運主管**帶得了；代理商 realm 給了就 422。沒填＝整個鍵不帶。
   */
  site_secrets?: Record<string, string> | null
}
export const approveHostedRequest = (id: string, input: ApproveHostedInput) =>
  api<{
    status: string
    request_id: string
    instance_id: string
    license_id: string
    site: { slug: string; domain: string | null; public_url: string | null; [k: string]: unknown }
    job: { job_id: string; kind: string; state: string; [k: string]: unknown }
    dns: Record<string, unknown>
    setup_url: string | null
    /** 批141 M 包：`setup_url` 帶一次性權杖（只顯示這一次）。 */
    setup_url_one_time: boolean
  } & UnknownModulesWarning>(`/api/admin/license-requests/${id}/approve-hosted`, {
    method: 'POST',
    body: input,
  })

export const listCustomerUsers = (customerId: string) =>
  api<CustomerUser[]>(`/api/admin/customers/${customerId}/users`)
export const addCustomerUser = (customerId: string, email: string) =>
  api<CustomerUser>(`/api/admin/customers/${customerId}/users`, { method: 'POST', body: { email } })
export const removeCustomerUser = (customerId: string, userId: string) =>
  api<null>(`/api/admin/customers/${customerId}/users/${userId}`, { method: 'DELETE' })


// --- 邀請碼（批81 需求 11 裁示 17）---

/**
 * 邀請碼清單（新到舊）。★ 批93-D 起是 **keyset 分頁**：回 `{items, next_cursor}`。
 * 碼的總量＝客戶數（每開一戶自動一組）＋我方手建的業務／活動碼，是成長型的。
 */
export const listInviteCodes = (opts: { limit?: number; cursor?: string | null } = {}) =>
  api<InviteCodePage>(
    `/api/admin/invite-codes${qs({
      limit: opts.limit ? String(opts.limit) : undefined,
      cursor: opts.cursor || undefined,
    })}`,
  )
/**
 * 建業務碼／活動碼。`code` 留空＝自動產 8 碼。
 *
 * 422 `customer_code_not_manual`＝客戶碼只能由系統產；422 `bad_code`＝字元集／長度不合
 * （不含 I、L、O、U）；409 `code_taken`＝撞碼。
 */
export const createInviteCode = (body: {
  kind: 'sales' | 'campaign'
  label: string
  note?: string | null
  code?: string | null
}) => api<InviteCodeRow>('/api/admin/invite-codes', { method: 'POST', body })
/** 啟用／停用。停用**客戶碼**＝那位客戶 CS 授權頁的邀請卡會消失（心跳不再下發）。 */
export const toggleInviteCode = (id: string, isActive: boolean) =>
  api<InviteCodeRow>(`/api/admin/invite-codes/${id}/toggle`, {
    method: 'POST',
    body: { is_active: isActive },
  })

// ---------------------------------------------------------------------------
// 模組加購子憑證（IPOADD1）——批93-B §2.3
//
// 綁的是**主機**（`instance_id`）不是主證：`licenses` 是 append-only，續約／換機都簽新
// `license_id`，綁主證的子證第一次續約就變孤兒。所以清單一律以主機為單位看。
// 清單／簽發回應**一律不帶 `blob`**（一頁三十張＝三十份可安裝的簽章明文進瀏覽器快取）；
// 要原文走 `downloadAddonBlob`（單張，後端記稽核）。
// ---------------------------------------------------------------------------

export interface LicenseAddon {
  addon_id: string
  customer_id: string
  product: string
  instance_id: string
  /** 簽發當下那張主證（advisory，產品端不比對）。 */
  parent_license_id: string | null
  label: string
  /** 加購的模組 map（值只能 `*` 或非空 csv）。 */
  modules: Record<string, string>
  /**
   * 子證上的配額值；與主證怎麼合併**依產品宣告的合併方式**（`QuotaSpec.addon_merge`，缺鍵＝取最大值
   * ——那時這是絕對值；`sum` 的鍵則是增加量）。null＝這張不動配額。
   */
  quotas: Record<string, number> | null
  seats: number | null
  max_product_lines: number | null
  /** 延後生效；null＝立即。 */
  not_before: string | null
  expires_at: string | null
  term_days: number | null
  /** 批108：期限的分鐘餘數（0–1439）。總期限＝`term_days * 1440 + term_minutes`。 */
  term_minutes: number
  trial: boolean
  /** `active`｜`revoked`｜`superseded`——**沒有 expired**（到期是 `expires_at` 推導）。 */
  status: string
  supersedes: string | null
  /**
   * 批155：簽發時用的子證模板 key（`license_addons.addon_plan`；自訂內容與批155 之前的舊列＝null）。
   * 加購表單的「取代」下拉用它只列同一個模板的子證——自訂內容的子證不能被取代
   * （權威仍在後端 409 `supersedes_invalid`）。舊後端不回這一欄＝undefined。
   */
  addon_plan?: string | null
  /** `admin`｜`partner`｜`portal`｜`system`（換機連帶重簽）｜`redeem`｜**`platform`**（批155：平台當場簽發）。 */
  source: string
  issued_by: string | null
  notes: string | null
  created_at: string
  revoked_at: string | null
}

/** 未知模組／配額鍵的警告（照簽，只提醒操作者）＋離線站提示。 */
export interface AddonIssueResult extends LicenseAddon {
  unknown_modules: string[]
  unknown_module_items: string[]
  unknown_quota_keys: string[]
  /** 非 null＝這台不心跳（從未回報／airgapped），撤銷不會自動生效。UI 必須顯示。 */
  offline_warning: string | null
}

/** 加購內容。`addon_plan_key` 給了就由**伺服器端**展開該套裝，其餘欄位仍可覆寫。 */
export interface AddonIssueInput {
  addon_plan_key?: string | null
  label?: string | null
  modules?: Record<string, string> | null
  /**
   * 送什麼依產品宣告的合併方式（`QuotaSpec.addon_merge`）：預設取最大值＝目標值（UI 的「現行 5000
   * ＋1000」助手算完送 6000）；`sum` 的鍵＝增加量（助手不讀主證）。見 `lib/addonQuotaInput.ts`。null 值後端 422。
   */
  quotas?: Record<string, number> | null
  seats?: number | null
  max_product_lines?: number | null
  /** `YYYY-MM-DD` 或 ISO8601；與**期限**（下面兩欄相加）至少一個。 */
  expires_at?: string | null
  /** 期限的「天」部分；批108 起 `0` 是合法值（配一個正的 `term_minutes`）。 */
  term_days?: number | null
  /** 期限的分鐘餘數（0–1439）。總期限＝`term_days * 1440 + term_minutes`。 */
  term_minutes?: number | null
  not_before?: string | null
  trial?: boolean
  notes?: string | null
  /**
   * 批155：取代同一台主機上的哪一張子證（`addon_id`）。必須是**同一個 instance、同一個子證模板、
   * 狀態 active**，否則 409 `supersedes_invalid`；簽發時舊張在同一筆交易改成「已被取代」。
   * 數值可以調小（例如容量減少）。
   */
  supersedes?: string | null
}

/** 這張主證**所在主機**的加購（含墓碑，新到舊）。 */
export const listLicenseAddons = (licenseId: string) =>
  api<LicenseAddon[]>(`/api/admin/licenses/${encodeURIComponent(licenseId)}/addons`)

/** 對某張主證所在的主機加購。主證沒綁機（離線／舊版證）→ 400，改用 `issueCustomerAddon`。 */
export const issueLicenseAddon = (licenseId: string, body: AddonIssueInput) =>
  api<AddonIssueResult>(`/api/admin/licenses/${encodeURIComponent(licenseId)}/addons`, {
    method: 'POST',
    body,
  })

export const listCustomerAddons = (customerId: string, instanceId?: string) =>
  api<LicenseAddon[]>(
    `/api/admin/customers/${customerId}/addons${qs({ instance_id: instanceId })}`,
  )

/** 顯式指定 `instance_id` 的加購（離線站、或主證沒綁機時的唯一入口）。 */
export const issueCustomerAddon = (
  customerId: string,
  body: AddonIssueInput & { instance_id: string; parent_license_id?: string | null },
  product?: string,
) =>
  api<AddonIssueResult>(`/api/admin/customers/${customerId}/addons${qs({ product })}`, {
    method: 'POST',
    body,
  })

/**
 * 下載單張子憑證原文（離線站貼進產品端）。**後端記稽核**。
 *
 * 「下載檔案」與「複製憑證」兩顆按鈕共用這一支——兩種帶走方式都該留下同一筆痕跡。
 */
export const downloadAddonBlob = (addonId: string) =>
  api<LicenseAddon & { blob: string }>(`/api/admin/addons/${encodeURIComponent(addonId)}/blob`)

export const revokeAddon = (addonId: string) =>
  api<LicenseAddon & { offline_warning: string | null }>(
    `/api/admin/addons/${encodeURIComponent(addonId)}/revoke`,
    { method: 'POST' },
  )

/** 續期：兩個都不給＝沿用舊張的 `term_days`（「再一期」）。 */
export const renewAddon = (
  addonId: string,
  body: { expires_at?: string | null; term_days?: number | null } = {},
) =>
  api<AddonIssueResult>(`/api/admin/addons/${encodeURIComponent(addonId)}/renew`, {
    method: 'POST',
    body,
  })

// ---------------------------------------------------------------------------
// 兌換碼（批104）——`api/admin_redeem.py`
//
// 三種效果（`kind`）：`extend` 延長主證期限／`trial` 簽一張試用子憑證／`grant` 發一整張
// 全新的試用主證（只開給「已連結 LS、還沒有任何授權」的站台）。
//
// ★ 建立與修改是 **superadmin**；發放、作廢、看清單與兌換紀錄是 admin 以上。前端照這條
//   線藏按鈕（後端 gate 才是真防線，藏的目的是不給一個必然 403 的入口）。
// ---------------------------------------------------------------------------

/** 活動效果的摘要（後端算好；**不含**內部備註）。逐鍵依 `kind` 而異。 */
export interface RedeemEffectSummary {
  kind: string
  days: number | null
  /** 批108：分鐘餘數（總長度＝`days * 1440 + minutes`）。 */
  minutes?: number | null
  /**
   * 批108：後端算好的期限人話（`30 分鐘`／`2 小時`／`3 天`）。
   * ★ 顯示一律用這個——光印 `days` 會讓分鐘級的效果顯示成「0 天」。
   */
  term_text?: string | null
  /** trial：子證名稱。 */
  label?: string | null
  /** grant：新證的套裝顯示名（活動自己的副本，不是 `product_plans` 的鍵）。 */
  plan_label?: string | null
  /** trial／grant：模組鍵（已排序）。 */
  modules?: string[]
  quotas?: Record<string, number> | null
  seats?: number | null
  max_product_lines?: number | null
  /** 兌換紀錄上的實際前後到期日（活動本身的摘要沒有這兩個）。 */
  old_expires_at?: string | null
  new_expires_at?: string | null
}

/** 活動內容（`effect`）——建立表單送的原始值，逐鍵規則見 `services/redeem.py`。 */
export interface RedeemEffect {
  days?: number
  /** 批108：分鐘餘數。與 `days` 相加＝效果的總長度（兩個都 0 會被後端擋）。 */
  minutes?: number
  label?: string
  plan_label?: string
  modules?: Record<string, string>
  quotas?: Record<string, number>
  seats?: number
  max_product_lines?: number
}

export interface RedeemCampaign {
  id: string
  name: string
  product: string
  /** null＝我方自營；非 null＝只開放這家代理商的客戶兌換。 */
  reseller_id: string | null
  kind: 'extend' | 'trial' | 'grant' | string
  effect: RedeemEffect
  effect_summary: RedeemEffectSummary
  /** `shared`＝一組共用碼；`unique`＝發放時才產生的不重複子碼。 */
  code_mode: 'shared' | 'unique' | string
  code_prefix: string | null
  /** null＝不限量。unique＝可發放的子碼總數；shared＝兌換次數上限。 */
  quantity: number | null
  starts_at: string | null
  ends_at: string | null
  stackable: boolean
  /**
   * 批107 使用條件：`deployment`＝每台部署各兌一次｜`customer`＝同一客戶只能兌一次。
   * 既有活動（批104 建的）一律 `deployment`；**新建的預設是 `customer`**。
   */
  once_per: string
  /** `any`｜`trial`｜`paid`（grant 不看這一項）。 */
  target_license: string
  eligible_plans: string[] | null
  status: 'active' | 'paused' | string
  redeemed_count: number
  issued_count: number
  created_by: string | null
  created_at: string
  updated_at: string | null
  /** 內部備註——**代理商 realm 的投影沒有這一欄**（`include_notes=False`）。 */
  notes?: string | null
  /** `shared` 模式的那一組共用碼（詳情／建立的回應才有）。 */
  shared_code?: RedeemCode | null
}

export interface RedeemCode {
  id: string
  campaign_id: string
  /** 正規化後的全串（去 `-`、大寫）——複製給客戶請用 `display_code`。 */
  code: string
  /** 人看的格式（有前綴時切法不同，**後端切好**，前端別自己拆）。 */
  display_code: string
  status: 'issued' | 'redeemed' | 'void' | string
  distributed_to_customer_id: string | null
  distributed_note: string | null
  issued_by: string | null
  /** `staff`＝我方發的（代理商清單上顯示「我方代發」）｜`reseller`＝代理商自己發的。 */
  issued_realm: string | null
  issued_at: string | null
  voided_by: string | null
  voided_at: string | null
  redeemed_at: string | null
  created_at: string
}

/** 一筆兌換紀錄＝**誰用**（actor）＋**用在哪**（instance／hostname／channel）。 */
export interface Redemption {
  redemption_id: string
  redeemed_at: string
  campaign_name: string | null
  kind: string
  effect_summary: RedeemEffectSummary
  /** `product`＝產品端 `/v1`／`portal`／`link`＝連結 LS 領取試用授權。 */
  channel: string
  instance_id: string
  hostname: string | null
  public_url: string | null
  customer_id: string | null
  customer_name: string | null
  actor_email: string | null
  actor_name: string | null
  /** portal／link 經 OAuth 驗過＝true；`/v1` 由產品端自報＝false。 */
  actor_verified: boolean
  app_version: string | null
  /** **代理商 realm 沒有這一欄**（兌換人的 IP 屬我方稽核資料）。 */
  client_ip?: string | null
  /** 後台看得到代碼本身（客戶端／產品端的投影不含它）。 */
  code?: string
}

export interface RedeemCampaignPage {
  items: RedeemCampaign[]
  next_cursor: string | null
}
export interface RedeemCodePage {
  items: RedeemCode[]
  /** 依狀態分組的張數（`issued`／`redeemed`／`void`），缺鍵＝0。 */
  counts: Record<string, number>
  next_cursor: string | null
}
export interface RedemptionPage {
  items: Redemption[]
  next_cursor: string | null
}

export interface RedeemListQuery {
  limit?: number
  cursor?: string | null
  product?: string
  reseller_id?: string
  status?: string
  kind?: string
}

export const listRedeemCampaigns = (opts: RedeemListQuery = {}) =>
  api<RedeemCampaignPage>(
    `/api/admin/redeem-campaigns${qs({
      limit: opts.limit ? String(opts.limit) : undefined,
      cursor: opts.cursor || undefined,
      product: opts.product || undefined,
      reseller_id: opts.reseller_id || undefined,
      status: opts.status || undefined,
      kind: opts.kind || undefined,
    })}`,
  )

/** 活動建立 body。`product`／`kind`／`code_mode`／`reseller_id`／`code_prefix` **建立後鎖定**。 */
export interface RedeemCampaignInput {
  name: string
  product: string
  kind: string
  effect: RedeemEffect
  code_mode: string
  notes?: string | null
  reseller_id?: string | null
  code_prefix?: string | null
  /** 只對 `shared` 有意義：留空＝自動產生。 */
  shared_code?: string | null
  quantity?: number | null
  starts_at?: string | null
  ends_at?: string | null
  stackable?: boolean
  /** 批107：`deployment`｜`customer`；不送＝後端預設 `customer`。 */
  once_per?: string
  target_license?: string
  eligible_plans?: string[] | null
}
/** 建立活動（**superadmin**）。422 `{code, message}`＝值域／代理商範圍不合。 */
export const createRedeemCampaign = (body: RedeemCampaignInput) =>
  api<RedeemCampaign>('/api/admin/redeem-campaigns', { method: 'POST', body })

export const getRedeemCampaign = (id: string) => api<RedeemCampaign>(`/api/admin/redeem-campaigns/${id}`)

/**
 * 修改活動（**superadmin**）。只收得動的欄在這裡；三個鎖定碼都是 422：
 * `effect_locked`（已有人兌換）／`quantity_below_used`／`bad_window`。
 * 顯式清掉限量要送 `unlimited: true`（`quantity: null` 在 JSON 裡與「沒送」分不出來）。
 */
export interface RedeemCampaignPatch {
  name?: string
  notes?: string | null
  effect?: RedeemEffect
  quantity?: number | null
  unlimited?: boolean
  starts_at?: string | null
  ends_at?: string | null
  clear_window?: boolean
  status?: string
  stackable?: boolean
  /** 批107：可改、**不追溯**（只影響之後的兌換，已兌的不撤）。 */
  once_per?: string
  target_license?: string
  eligible_plans?: string[] | null
}
export const updateRedeemCampaign = (id: string, body: RedeemCampaignPatch) =>
  api<RedeemCampaign>(`/api/admin/redeem-campaigns/${id}`, { method: 'PUT', body })

export const listRedeemCodes = (
  id: string,
  opts: { limit?: number; cursor?: string | null; status?: string } = {},
) =>
  api<RedeemCodePage>(
    `/api/admin/redeem-campaigns/${id}/codes${qs({
      limit: opts.limit ? String(opts.limit) : undefined,
      cursor: opts.cursor || undefined,
      status: opts.status || undefined,
    })}`,
  )

/** 發放 N 組不重複子碼（1–500）。回新產生的碼＋更新後的活動（名額計數）。 */
export const issueRedeemCodes = (
  id: string,
  body: { count: number; to_customer_id?: string | null; note?: string | null },
) =>
  api<{ items: RedeemCode[]; campaign: RedeemCampaign }>(`/api/admin/redeem-campaigns/${id}/codes`, {
    method: 'POST',
    body,
  })

/** 作廢一組尚未兌換的子碼（釋回一格發放名額）。409＝已兌換或已作廢。 */
export const voidRedeemCode = (id: string, codeId: string) =>
  api<{ code: RedeemCode; campaign: RedeemCampaign }>(
    `/api/admin/redeem-campaigns/${id}/codes/${codeId}/void`,
    { method: 'POST' },
  )

export const listRedemptions = (
  id: string,
  opts: { limit?: number; cursor?: string | null; customer_id?: string } = {},
) =>
  api<RedemptionPage>(
    `/api/admin/redeem-campaigns/${id}/redemptions${qs({
      limit: opts.limit ? String(opts.limit) : undefined,
      cursor: opts.cursor || undefined,
      customer_id: opts.customer_id || undefined,
    })}`,
  )

// ---------------------------------------------------------------------------
// 批141：客戶來源篩選、批次核准
// ---------------------------------------------------------------------------

/** 客戶清單的來源篩選（`GET /customers?source=`）。 */
export type CustomerSource = 'all' | 'direct' | 'platform'

/** 批次核准的上限（與後端 `BATCH_APPROVE_MAX` 同值）。 */
export const BATCH_APPROVE_MAX = 200

export interface BatchApproveFailure {
  id: string
  /** 失敗原因碼（`not_batchable`／`use_approve_hosted`／`policy_required`／`conflict`…）。 */
  code: string
  message: string
  http_status?: number
  [key: string]: unknown
}

export interface BatchApproveResult {
  batch_id: string
  approved: { id: string; license_id: string | null; addon_id?: string | null }[]
  failed: BatchApproveFailure[]
}

/**
 * 批次核准待審核的申請（批141，裁示 30／40）。admin 以上，**逐筆**套核發政策（套裝政策
 * 高於自己的那幾筆會個別失敗，其餘照常）。**代管開站申請一律排除**（`use_approve_hosted`）。
 * 一筆一交易：失敗的那筆 rollback、成功的留著；回應順序＝`ids` 的順序。
 */
export const batchApproveLicenseRequests = (ids: string[], reviewNote?: string | null) =>
  api<BatchApproveResult>('/api/admin/license-requests/batch-approve', {
    method: 'POST',
    body: { ids, ...(reviewNote ? { review_note: reviewNote } : {}) },
  })

// ---------------------------------------------------------------------------
// 批147：超級管理者的 API 金鑰（〈系統 › API 金鑰〉）
// ---------------------------------------------------------------------------
//
// 金鑰沿用網頁同一組 `/api/admin/*`，權限＝擁有者（必為超級管理者）∩ 金鑰的 scope。
// 下面這幾支管理端點**只認網頁登入**、只有超級管理者（金鑰不能管理金鑰）。
// 勾選邏輯在 `lib/apiKeyScopes.ts`、表單草稿在 `lib/apiKeyDraft.ts`。

/** 伺服器端算的狀態（前端不推導）：撤銷優先，其次看 `expires_at` 過了沒。 */
export type ApiKeyStatus = 'active' | 'revoked' | 'expired'

/**
 * 撤銷原因。`manual`＝有人在後台按了撤銷；其餘四種是**擁有者帳號異動時自動連坐**
 * （停用／降級／強制登出／刪除）。
 */
export type ApiKeyRevokeReason =
  | 'manual'
  | 'owner_inactive'
  | 'owner_demoted'
  | 'owner_force_logout'
  | 'owner_deleted'

export interface ApiKeyRow {
  id: string
  name: string
  /** 明文的前 16 碼（含 `ipols_`），畫面上一律接「…」顯示。 */
  prefix: string
  owner_id: string
  owner_email: string | null
  scopes: string[]
  status: ApiKeyStatus
  /** null＝無期限（主人裁示）。 */
  expires_at: string | null
  last_used_at: string | null
  last_used_ip: string | null
  revoked_at: string | null
  revoked_by: string | null
  revoked_reason: ApiKeyRevokeReason | string | null
  created_at: string
  created_by: string | null
}

/** 建立的回應：多一個 `key`＝**明文，只會出現這一次**。 */
export interface ApiKeyCreated extends ApiKeyRow {
  key: string
}

export interface ApiKeyScopeAction {
  /** `<類>:read`／`<類>:write`。 */
  scope: string
  action: 'read' | 'write'
  label: string
  /** 這個動作涵蓋幾支端點（勾選格旁顯示）。 */
  endpoints: number
}

export interface ApiKeyScopeCategory {
  key: string
  label: string
  /** 有些類只有 `read`（員工帳號、稽核、SQL、log、tunnel）。 */
  actions: ApiKeyScopeAction[]
}

/** 網頁專屬端點（金鑰一律不能做），勾選格底下唯讀列給人看。 */
export interface ApiKeyWebOnlyEntry {
  method: string
  path: string
  label: string
}

export interface ApiKeyScopeCatalog {
  categories: ApiKeyScopeCategory[]
  web_only: ApiKeyWebOnlyEntry[]
}

/** 勾選格的目錄（類別順序＝後端 `SCOPE_CATEGORIES`）。 */
export const apiKeyScopeCatalog = () => api<ApiKeyScopeCatalog>('/api/admin/api-keys/scopes')

/** 全部超級管理者的金鑰（新到舊）。`status` 不帶＝全部。 */
export const listApiKeys = (opts: { status?: ApiKeyStatus; owner_id?: string } = {}) =>
  api<ApiKeyRow[]>(`/api/admin/api-keys${qs({ status: opts.status, owner_id: opts.owner_id })}`)

/**
 * 建一把**自己的**金鑰。`expires_at` 省略或 null＝無期限；有填必須是未來。
 * 422 `{code}`：`unknown_scope`／`scopes_required`／`expires_in_past`。
 */
export const createApiKey = (body: { name: string; scopes: string[]; expires_at?: string | null }) =>
  api<ApiKeyCreated>('/api/admin/api-keys', { method: 'POST', body })

/**
 * 改**自己的**金鑰——body **只帶要改的欄位**（`expires_at: null`＝改成無期限）。
 * 403 `not_owner`／409 `key_revoked`／422 同建立。
 */
export const updateApiKey = (
  id: string,
  body: { name?: string; scopes?: string[]; expires_at?: string | null },
) => api<ApiKeyRow>(`/api/admin/api-keys/${id}`, { method: 'PUT', body })

/** 撤銷（任何超級管理者都能撤銷任何一把；已撤銷再按＝原樣回傳）。不可復原。 */
export const revokeApiKey = (id: string) =>
  api<ApiKeyRow>(`/api/admin/api-keys/${id}/revoke`, { method: 'POST' })

export interface ApiKeyAccessLogRow {
  id: number
  /** null＝認證失敗（查無此鑰），此時 `key_prefix` 是「嘗試用的前綴」。 */
  api_key_id: string | null
  key_prefix: string | null
  method: string
  path: string
  status: number
  ip: string | null
  created_at: string
}

export interface ApiKeyAccessLogCursor {
  at: string
  id: number
}

export interface ApiKeyAccessLogPage {
  rows: ApiKeyAccessLogRow[]
  /** 帶回 `before_at`／`before_id` 就是下一頁；null＝沒有了。 */
  next: ApiKeyAccessLogCursor | null
}

/**
 * 金鑰存取紀錄（新到舊，含 401／403／429）。後端**一律帶時間窗**：`since` 不帶＝最近 24 小時。
 */
export const listApiKeyAccessLogs = (
  q: {
    key_id?: string
    status?: number
    since?: string
    until?: string
    cursor?: ApiKeyAccessLogCursor | null
    limit?: number
  } = {},
) =>
  api<ApiKeyAccessLogPage>(
    `/api/admin/api-key-access-logs${qs({
      key_id: q.key_id,
      status: q.status !== undefined ? String(q.status) : undefined,
      since: q.since,
      until: q.until,
      before_at: q.cursor ? q.cursor.at : undefined,
      before_id: q.cursor ? String(q.cursor.id) : undefined,
      limit: q.limit !== undefined ? String(q.limit) : undefined,
    })}`,
  )
